
import { recordRecipeAuditLog, fetchRecipeAdminMetadata, ACTION_TYPES } from "./auditLogger.js";
import { loadRecipeRatings, renderRecipeRatings } from "./recipeRatings.js";
import { checkRecipeEditable } from "./recipeEditGuard.js";
import { buildRecipePhotoPromptWithMetadata } from "./recipePhotoStyles.js";
import { BADGE, BADGE_DOT, BUTTON, FIELD, PILL, checkbox } from "./ui/classes.js";
import { solidIcon } from "./ui/icons.js";
import { jsArg } from "./ui/copy.js";
import { showDialog, hideDialog } from "./ui/dialog.js";
import {
  initAudioContext,
  requestNotificationPermission,
  getNotificationPermission,
  notifyRecipeReady,
  notifyRecipeError,
  notifyPhotoReady
} from "./utils/notifications.js";

// Text from recipes and the AI goes through this before it lands in markup.
function escapeHtml(value) {
  return String(value ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);
}

/**
 * Recipe Catalog & Editor Controller Module
 * Handles loading pure HTML partial templates, recipe table rendering, search/filter, and dynamic ingredient/instruction step editing.
 */

export async function loadRecipesTemplate() {
  const container = document.getElementById("sectionRecipesContainer");
  if (!container) return;

  if (document.getElementById("sectionRecipes")) return;

  try {
    const resp = await fetch("templates/recipes.html");
    if (!resp.ok) {
      throw new Error(`Failed to load recipes template: ${resp.status} ${resp.statusText}`);
    }
    const html = await resp.text();
    container.innerHTML = html;
  } catch (err) {
    console.error("❌ Recipes Template Load Error:", err);
    container.innerHTML = `<div class="rounded-md bg-red-50 p-4 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-300">Failed to load the Recipes template: ${err.message}</div>`;
  }
}

export async function loadRecipeViewTemplate(force = false) {
  const container = document.getElementById("sectionRecipeViewContainer");
  if (!container) return;

  if (!force && document.getElementById("sectionRecipeView") && document.getElementById("rvHeroImage")) return;

  try {
    const resp = await fetch("templates/recipeView.html?v=" + Date.now());
    if (!resp.ok) {
      throw new Error(`Failed to load recipe view template: ${resp.status} ${resp.statusText}`);
    }
    const html = await resp.text();
    container.innerHTML = html;
  } catch (err) {
    console.error("❌ Recipe View Template Load Error:", err);
    container.innerHTML = `<div class="rounded-md bg-red-50 p-4 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-300">Failed to load the recipe view template: ${err.message}</div>`;
  }
}

export function viewRecipe(id) {
  if (!id) return;
  if (typeof window !== "undefined") {
    window.location.hash = `#recipe-view?id=${encodeURIComponent(id)}`;
  }
}

let currentRecipeViewData = null;

/** The recipe document the recipe view has loaded (null when none). Used by the edit guard in app.js. */
export function getCurrentRecipeViewData() {
  return currentRecipeViewData;
}
let activeTimelineKey = "quickest_timeline";
let activeVariantKey = "default_variant";

export function toggleRecipeViewRawJson() {
  const card = document.getElementById("rvRawJsonCard");
  if (!card) return;
  const isHidden = card.style.display === "none";
  card.style.display = isHidden ? "block" : "none";
}

export function setRecipeViewTimeline(timelineKey) {
  activeTimelineKey = timelineKey;
  renderRecipeViewInstructions();
}

export function setRecipeViewVariant(variantKey) {
  activeVariantKey = variantKey;
  renderRecipeViewInstructions();
}

function parseInstructionStep(stepText) {
  if (typeof stepText !== "string") return "";
  return stepText.replace(/\[quantity:([^,\]]+),unit:([^\]]+)\]/g, (match, q, u) => {
    return `<strong class="rounded bg-green-50 px-1.5 py-0.5 font-semibold text-green-800 ring-1 ring-green-600/20 ring-inset dark:bg-green-500/10 dark:text-green-300 dark:ring-green-400/20">${q.trim()} ${u.trim()}</strong>`;
  });
}

// The instruction variants' colours, light and dark (#1165: the old pastel boxes kept their light
// background in dark mode). Whole class strings, so Tailwind finds them.
const VARIANT_TONES = {
  teal: {
    card: "flex flex-col gap-3 rounded-lg bg-teal-50 p-4 ring-1 ring-teal-600/15 dark:bg-teal-500/10 dark:ring-teal-400/20",
    head: "flex flex-wrap items-center justify-between gap-2 border-b border-teal-600/15 pb-2 dark:border-teal-400/20",
    title: "text-sm font-semibold text-teal-800 dark:text-teal-300",
    chip: "inline-flex items-center gap-x-1 rounded-md bg-white px-2 py-0.5 text-xs font-medium text-gray-700 ring-1 ring-teal-600/20 ring-inset dark:bg-white/5 dark:text-gray-200 dark:ring-teal-400/20"
  },
  sky: {
    card: "flex flex-col gap-3 rounded-lg bg-sky-50 p-4 ring-1 ring-sky-600/15 dark:bg-sky-500/10 dark:ring-sky-400/20",
    head: "flex flex-wrap items-center justify-between gap-2 border-b border-sky-600/15 pb-2 dark:border-sky-400/20",
    title: "text-sm font-semibold text-sky-800 dark:text-sky-300",
    chip: "inline-flex items-center gap-x-1 rounded-md bg-white px-2 py-0.5 text-xs font-medium text-gray-700 ring-1 ring-sky-600/20 ring-inset dark:bg-white/5 dark:text-gray-200 dark:ring-sky-400/20"
  },
  green: {
    card: "flex flex-col gap-3 rounded-lg bg-green-50 p-4 ring-1 ring-green-600/15 dark:bg-green-500/10 dark:ring-green-400/20",
    head: "flex flex-wrap items-center justify-between gap-2 border-b border-green-600/15 pb-2 dark:border-green-400/20",
    title: "text-sm font-semibold text-green-800 dark:text-green-300",
    chip: "inline-flex items-center gap-x-1 rounded-md bg-white px-2 py-0.5 text-xs font-medium text-gray-700 ring-1 ring-green-600/20 ring-inset dark:bg-white/5 dark:text-gray-200 dark:ring-green-400/20"
  }
};
const STEPS_LIST = "list-decimal space-y-3 pl-5 text-sm/6 text-gray-800 marker:font-semibold marker:text-gray-400 dark:text-gray-200";
const NO_STEPS = `<li class="list-none text-sm text-gray-500 italic dark:text-gray-400">No steps written for this variant.</li>`;

function renderRecipeViewInstructions() {
  if (!currentRecipeViewData) return;

  const gridEl = document.getElementById("rvInstructionMatrixGrid");
  if (!gridEl) return;

  const timelines = currentRecipeViewData.instructions || {};
  // The catalogue view shows Fastest and Dish Saver only (#2451): not a recipe's Prep ahead version (`prep_ahead_variant`,
  // which nothing in the apps cooks from since then; whether it stays is open, #2454), nor a legacy Sunday Surge one.
  const isModern = Boolean(timelines.fastest_variant || timelines.dish_saver_variant);

  if (isModern) {
    gridEl.style.gridTemplateColumns = "repeat(auto-fit, minmax(320px, 1fr))";

    const modernVariants = [
      { id: "fastest_variant", title: "Fastest flow (standard cook)", tone: VARIANT_TONES.teal, variantData: timelines.fastest_variant },
      { id: "dish_saver_variant", title: "Dish Saver flow", tone: VARIANT_TONES.sky, variantData: timelines.dish_saver_variant }
    ].filter(v => Boolean(v.variantData));

    gridEl.innerHTML = modernVariants.map(v => {
      const data = v.variantData;
      const metrics = currentRecipeViewData.planning_metrics || {};
      const prep = Number(data.prep_time_minutes || metrics.prep_time_minutes || currentRecipeViewData.prep_time_minutes || 0);
      const cook = Number(data.cook_time_minutes || metrics.cook_time_minutes || currentRecipeViewData.cook_time_minutes || 0);
      const total = Number(data.total_time_minutes || (prep + cook) || metrics.total_time_minutes || currentRecipeViewData.total_time_minutes || 0);
      const dishScore = Number(data.dish_load_score || metrics.dish_load_score || currentRecipeViewData.dish_load_score || 1);
      const cookware = Array.isArray(data.cookware) ? data.cookware : [];
      const steps = Array.isArray(data.steps) ? data.steps : [];

      const stepsHtml = steps.length === 0 ? NO_STEPS : steps.map((step) => {
        const rawText = typeof step === "string" ? step : (step.instruction || step.text || "");
        const callouts = Array.isArray(step.ingredients_callouts) ? step.ingredients_callouts : [];
        const calloutsHtml = callouts.length > 0
          ? `<div class="mt-1.5 flex flex-wrap gap-1.5">${callouts.map(c => `<span class="${v.tone.chip}">${c.name || c.ingredient_id}${c.amount ? ` <span class="font-normal text-gray-500 dark:text-gray-400">(${c.amount} ${c.unit || ""})</span>` : ""}</span>`).join("")}</div>`
          : "";
        return `<li><div>${parseInstructionStep(rawText)}</div>${calloutsHtml}</li>`;
      }).join("");

      const cookwareHtml = cookware.length > 0
        ? `<div class="flex flex-wrap items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400"><span class="font-medium text-gray-700 dark:text-gray-300">Tools:</span>${cookware.map(cw => `<span class="${v.tone.chip}">${cw}</span>`).join("")}</div>`
        : "";

      return `
        <div class="${v.tone.card}">
          <div class="${v.tone.head}">
            <span class="${v.tone.title}">${v.title}</span>
            <div class="flex flex-wrap items-center gap-1.5">
              <span class="${v.tone.chip}">${prep} min prep · ${cook} min cook (${total} min)</span>
              <span class="${v.tone.chip}">Dishes ${dishScore}/5</span>
            </div>
          </div>
          ${cookwareHtml}
          <ol class="${STEPS_LIST}">${stepsHtml}</ol>
        </div>
      `;
    }).join("");
    return;
  }

  gridEl.style.gridTemplateColumns = "repeat(2, 1fr)";
  const quickestTL = timelines.quickest_timeline || {};
  const dishSaverTL = timelines.min_dishes_timeline || timelines.dish_saver_timeline || {};

  const quadrants = [
    { id: "quickest_default", title: "Fastest, standard", tone: VARIANT_TONES.teal, variantData: quickestTL.default_variant || {} },
    { id: "dish_saver_default", title: "Dish Saver, standard", tone: VARIANT_TONES.sky, variantData: dishSaverTL.default_variant || {} }
  ];

  gridEl.innerHTML = quadrants.map(q => {
    const v = q.variantData;
    const prep = v.prep_time ?? currentRecipeViewData.planning_metrics?.prep_time_minutes ?? 0;
    const cook = v.cook_time ?? currentRecipeViewData.planning_metrics?.cook_time_minutes ?? 0;
    const total = prep + cook;
    const rawSteps = v.instructions || v.steps || [];
    const stepsHtml = rawSteps.length === 0 ? NO_STEPS : rawSteps.map(step => {
      const text = typeof step === "string" ? step : step.instruction || step.text || "";
      return `<li>${parseInstructionStep(text)}</li>`;
    }).join("");

    return `
      <div class="${q.tone.card}">
        <div class="${q.tone.head}">
          <span class="${q.tone.title}">${q.title}</span>
          <span class="${q.tone.chip}">Prep ${prep} min · Cook ${cook} min · ${total} min</span>
        </div>
        <ol class="${STEPS_LIST}">${stepsHtml}</ol>
      </div>
    `;
  }).join("");
}

let activeCategoriesData = {};

export function updateActiveCategoriesData(categoriesData = {}) {
  activeCategoriesData = categoriesData;
}

export function resolveCategoryTag(tagId, categoriesData = activeCategoriesData) {
  if (!tagId) return null;
  const cleanId = String(tagId).trim().toLowerCase();
  let catObj = (categoriesData && Object.keys(categoriesData).length > 0) ? categoriesData : activeCategoriesData;
  if ((!catObj || Object.keys(catObj).length === 0) && typeof window !== "undefined" && window.crmApp && window.crmApp.getCategoriesData) {
    catObj = window.crmApp.getCategoriesData() || {};
  }

  const recipeCats = catObj.recipe_categories || [];
  for (const cat of recipeCats) {
    if (cat.tags && Array.isArray(cat.tags)) {
      const match = cat.tags.find(t => t.id && String(t.id).trim().toLowerCase() === cleanId);
      if (match) return match.name || match.id;
    }
  }

  if (catObj.dietary_preferences && Array.isArray(catObj.dietary_preferences)) {
    const match = catObj.dietary_preferences.find(d => d.id && String(d.id).trim().toLowerCase() === cleanId);
    if (match) return match.name || match.id;
  }

  if (catObj.allergens && Array.isArray(catObj.allergens)) {
    const match = catObj.allergens.find(a => a.id && String(a.id).trim().toLowerCase() === cleanId);
    if (match) return match.name || match.id;
  }

  return null;
}

export function renderCategoryChip(tagId, categoriesData = activeCategoriesData) {
  if (!tagId) return '<span class="text-gray-400 dark:text-gray-500">None</span>';
  const name = resolveCategoryTag(tagId, categoriesData);

  if (name) {
    return `<span class="${BADGE.gray}">${name}</span>`;
  } else {
    return `<span class="${BADGE.yellow} cursor-help" title="'${tagId}' is missing from the categories">${solidIcon("exclamation-triangle", "size-3.5")}${tagId}</span>`;
  }
}

let activeMasterIngredientsMap = {};

export function updateActiveMasterIngredientsData(masterIngredientsMap = {}) {
  if (masterIngredientsMap && Object.keys(masterIngredientsMap).length > 0) {
    activeMasterIngredientsMap = masterIngredientsMap;
  }
}

// ratingsReads: { collection, getDocs } for the Ratings card (#2173); without them the card stays as it loads.
export async function renderRecipeViewSection(db, getDoc, doc, showToast = null, masterIngredientsMap = {}, categoriesData = {}, ratingsReads = null) {
  if (masterIngredientsMap && Object.keys(masterIngredientsMap).length > 0) {
    activeMasterIngredientsMap = masterIngredientsMap;
  }
  if (categoriesData && Object.keys(categoriesData).length > 0) {
    activeCategoriesData = categoriesData;
  }
  await loadRecipeViewTemplate();

  const secView = document.getElementById("sectionRecipeView");
  if (secView) secView.style.display = "block";

  const hash = window.location.hash.replace("#", "").trim();
  let recipeId = "";
  if (hash.includes("?id=")) {
    recipeId = hash.split("?id=")[1].split("&")[0];
  } else if (hash.includes("id=")) {
    recipeId = new URLSearchParams(hash.split("?")[1]).get("id");
  } else {
    recipeId = new URLSearchParams(window.location.search).get("id");
  }

  recipeId = decodeURIComponent(recipeId || "").trim();

  const loadingStatus = document.getElementById("recipeViewLoadingStatus");
  const errorCard = document.getElementById("recipeViewErrorContainer");
  const mainContent = document.getElementById("recipeViewMainContent");

  if (!recipeId) {
    if (loadingStatus) loadingStatus.style.display = "none";
    if (errorCard) {
      errorCard.style.display = "block";
      document.getElementById("recipeViewErrorTitle").textContent = "No Recipe ID Provided";
      document.getElementById("recipeViewErrorMessage").textContent = "Example usage: #recipe-view?id=recipe_001";
    }
    if (mainContent) mainContent.style.display = "none";
    return;
  }

  if (loadingStatus) loadingStatus.style.display = "block";
  if (errorCard) errorCard.style.display = "none";
  if (mainContent) mainContent.style.display = "none";

  try {
    const docRef = doc(db, "recipes", recipeId);
    const docSnap = await getDoc(docRef);

    if (loadingStatus) loadingStatus.style.display = "none";
    if (!docSnap.exists()) {
      if (errorCard) {
        errorCard.style.display = "block";
        document.getElementById("recipeViewErrorTitle").textContent = `Recipe Not Found: '${recipeId}'`;
        document.getElementById("recipeViewErrorMessage").textContent = `Document '/recipes/${recipeId}' does not exist in Firestore.`;
      }
      return;
    }

    currentRecipeViewData = docSnap.data();
    populateRecipeViewDOM(currentRecipeViewData, recipeId);
    if (mainContent) mainContent.style.display = "block";
    renderRecipeRatings(undefined);

    // Fetch and render admin audit log metadata
    try {
      const metaData = await fetchRecipeAdminMetadata({ db, doc, getDoc, recipeId });
      renderRecipeAuditLogDOM(metaData);
    } catch (auditErr) {
      console.warn("⚠️ Failed to load recipe admin audit metadata:", auditErr);
      renderRecipeAuditLogDOM(null);
    }

    // The recipe's ratings (#2173)
    if (ratingsReads) {
      try {
        renderRecipeRatings(await loadRecipeRatings({ db, recipeId, ...ratingsReads }));
      } catch (ratingsErr) {
        console.warn("⚠️ Failed to load the recipe's ratings:", ratingsErr);
        renderRecipeRatings(null, ratingsErr);
      }
    }
  } catch (err) {
    console.error("Failed to fetch recipe from Firestore:", err);
    if (loadingStatus) loadingStatus.style.display = "none";
    if (errorCard) {
      errorCard.style.display = "block";
      document.getElementById("recipeViewErrorTitle").textContent = "Error Loading Recipe";
      document.getElementById("recipeViewErrorMessage").textContent = err.message;
    }
  }
}

export function formatAuditTimestamp(rawDate) {
  if (!rawDate) return "--";
  let dateObj = null;
  if (typeof rawDate === "object" && rawDate.seconds) {
    dateObj = new Date(rawDate.seconds * 1000);
  } else if (typeof rawDate === "object" && typeof rawDate.toDate === "function") {
    dateObj = rawDate.toDate();
  } else if (typeof rawDate === "string" || typeof rawDate === "number") {
    dateObj = new Date(rawDate);
  }
  if (!dateObj || isNaN(dateObj.getTime())) return String(rawDate || "--");
  return dateObj.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  }) + " " + dateObj.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true
  });
}

const AUDIT_EMPTY = `<p class="rounded-md border-2 border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500 dark:border-white/10 dark:text-gray-400">No audit records for this recipe yet.</p>`;

// Badge colour per audit action.
const AUDIT_ACTIONS = {
  source_updated: ["Source link updated", BADGE.blue],
  status_transition: ["Status changed", BADGE.green],
  photo_regenerated: ["Photo regenerated", BADGE.paprika],
  photo_uploaded: ["Photo uploaded", BADGE.paprika],
  cookware_updated: ["Cookware updated", BADGE.blue],
  recipe_edited: ["Recipe edited", BADGE.yellow],
  recipe_created: ["Recipe created", BADGE.green]
};

export function renderRecipeAuditLogDOM(metaData) {
  const versionBadge = document.getElementById("rvAuditVersionBadge");
  const createdByEl = document.getElementById("rvAuditCreatedBy");
  const createdAtEl = document.getElementById("rvAuditCreatedAt");
  const modifiedByEl = document.getElementById("rvAuditModifiedBy");
  const modifiedAtEl = document.getElementById("rvAuditModifiedAt");
  const timelineContainer = document.getElementById("rvAdminAuditTimeline");
  const sourceContentEl = document.getElementById("rvAuditSourceContent");
  const sourceLinkEl = document.getElementById("rvAuditSourceLink");
  const sourceActionsEl = document.getElementById("rvAuditSourceActions");

  if (!timelineContainer) return;

  if (!metaData) {
    if (versionBadge) versionBadge.textContent = "v1";
    if (createdByEl) createdByEl.textContent = "--";
    if (createdAtEl) createdAtEl.textContent = "--";
    if (modifiedByEl) modifiedByEl.textContent = "--";
    if (modifiedAtEl) modifiedAtEl.textContent = "--";
    if (sourceContentEl) {
      sourceContentEl.innerHTML = `<span class="italic">No origin recorded</span>`;
    }
    if (sourceActionsEl) sourceActionsEl.style.display = "none";

    timelineContainer.innerHTML = AUDIT_EMPTY;
    return;
  }

  // Source Origin Handling
  if (sourceContentEl) {
    const sourceObj = metaData.source || {};
    const sourceUrl = sourceObj.url || metaData.source_url || null;
    let sourceDomain = sourceObj.domain || metaData.source_domain || "";
    if (!sourceDomain && sourceUrl) {
      try {
        sourceDomain = new URL(sourceUrl).hostname.replace(/^www\./, "");
      } catch (_) {}
    }

    if (sourceUrl) {
      sourceContentEl.innerHTML = `<span class="${BADGE.blue} mr-2">${solidIcon("link", "size-3")}${sourceDomain || "Web source"}</span><span class="text-xs break-all text-gray-700 dark:text-gray-300">${sourceUrl}</span>`;
      if (sourceLinkEl) {
        sourceLinkEl.href = sourceUrl;
      }
      if (sourceActionsEl) {
        sourceActionsEl.style.display = "flex";
      }
    } else if (sourceObj.type === "text") {
      sourceContentEl.innerHTML = `<span class="italic">Imported from text (no web link)</span>`;
      if (sourceActionsEl) sourceActionsEl.style.display = "none";
    } else {
      sourceContentEl.innerHTML = `<span class="italic">Written by hand or a catalog original (no web link)</span>`;
      if (sourceActionsEl) sourceActionsEl.style.display = "none";
    }
  }

  // Version
  const version = typeof metaData.version === "number" ? metaData.version : 1;
  if (versionBadge) versionBadge.textContent = `v${version}`;

  // Creator Info
  const creatorUser = metaData.created_by || {};
  const creatorEmail = creatorUser.email || creatorUser.display_name || creatorUser.uid || "--";
  if (createdByEl) createdByEl.textContent = creatorEmail;
  if (createdAtEl) createdAtEl.textContent = formatAuditTimestamp(metaData.created_at);

  // Last Modified Info
  const modifierUser = metaData.last_modified_by || {};
  const modifierEmail = modifierUser.email || modifierUser.display_name || modifierUser.uid || "--";
  if (modifiedByEl) modifiedByEl.textContent = modifierEmail;
  if (modifiedAtEl) modifiedAtEl.textContent = formatAuditTimestamp(metaData.last_modified_at);

  // Change Log Events
  const changeLog = Array.isArray(metaData.change_log) ? metaData.change_log : [];
  if (changeLog.length === 0) {
    timelineContainer.innerHTML = AUDIT_EMPTY;
    return;
  }

  timelineContainer.innerHTML = `<ul role="list" class="-mb-6">${changeLog.map((event, index) => {
    const key = String(event.action || "");
    const known = AUDIT_ACTIONS[key];
    const actionLabel = known ? known[0] : (key || "Action").replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
    const badge = known ? known[1] : BADGE.gray;

    const performer = event.performed_by || {};
    const adminEmail = performer.email || performer.display_name || performer.uid || "Admin";
    const formattedTime = formatAuditTimestamp(event.timestamp);
    const summary = event.summary || "Recipe updated";
    const eventVersion = typeof event.version === "number" ? `v${event.version}` : "";

    let detailsHtml = "";
    if (event.details && typeof event.details === "object" && Object.keys(event.details).length > 0) {
      const detailEntries = Object.entries(event.details)
        .filter(([_, v]) => v !== null && v !== undefined && v !== "")
        .map(([k, v]) => {
          const valStr = typeof v === "object" ? JSON.stringify(v) : String(v);
          return `<span class="inline-flex items-center gap-x-1 rounded-md bg-gray-50 px-2 py-0.5 text-xs text-gray-600 ring-1 ring-gray-500/10 ring-inset dark:bg-white/5 dark:text-gray-300 dark:ring-white/10"><span>${k.replace(/_/g, " ")}:</span><span class="font-mono font-semibold text-gray-900 dark:text-white">${valStr}</span></span>`;
        });
      if (detailEntries.length > 0) {
        detailsHtml = `<div class="mt-2 flex flex-wrap gap-1.5">${detailEntries.join("")}</div>`;
      }
    }

    const isLast = index === changeLog.length - 1;
    return `
      <li>
        <div class="relative pb-6">
          ${isLast ? "" : `<span aria-hidden="true" class="absolute top-5 left-2.5 -ml-px h-full w-0.5 bg-gray-200 dark:bg-white/10"></span>`}
          <div class="relative flex items-start gap-x-3">
            <span class="mt-1 flex size-5 flex-none items-center justify-center rounded-full bg-white ring-1 ring-gray-300 dark:bg-gray-900 dark:ring-white/15"><span class="size-1.5 rounded-full bg-gray-400 dark:bg-gray-500"></span></span>
            <div class="min-w-0 flex-1">
              <div class="flex flex-wrap items-center justify-between gap-2">
                <div class="flex flex-wrap items-center gap-2 text-sm">
                  <span class="${badge}">${actionLabel}</span>
                  ${eventVersion ? `<span class="${BADGE.gray}">${eventVersion}</span>` : ""}
                  <span class="text-gray-500 dark:text-gray-400">by <span class="font-medium text-gray-900 dark:text-white">${adminEmail}</span></span>
                </div>
                <time class="font-mono text-xs text-gray-500 dark:text-gray-400">${formattedTime}</time>
              </div>
              <p class="mt-1 text-sm text-gray-700 dark:text-gray-300">${summary}</p>
              ${detailsHtml}
            </div>
          </div>
        </div>
      </li>
    `;
  }).join("")}</ul>`;
}

export async function refreshRecipeAuditLog(db, doc, getDoc, recipeId) {
  if (!recipeId || !db || !doc || !getDoc) return;
  try {
    const metaData = await fetchRecipeAdminMetadata({ db, doc, getDoc, recipeId });
    renderRecipeAuditLogDOM(metaData);
  } catch (err) {
    console.warn("⚠️ Failed to refresh recipe audit log:", err);
  }
}

export function copyRecipeSourceUrl(showToast = null) {
  const sourceLinkEl = document.getElementById("rvAuditSourceLink");
  const copyBtn = document.getElementById("rvAuditCopySourceBtn");
  const url = sourceLinkEl?.href;
  if (!url || url === "#" || url.endsWith("#")) {
    if (showToast) showToast("⚠️ No external source URL to copy.", "warning");
    return;
  }
  navigator.clipboard.writeText(url).then(() => {
    if (copyBtn) copyBtn.textContent = "Copied";
    if (showToast) showToast("✓ Copied source URL to clipboard!", "success");
    setTimeout(() => {
      if (copyBtn) copyBtn.textContent = "Copy link";
    }, 2000);
  }).catch((err) => {
    console.warn("Clipboard write failed:", err);
    if (showToast) showToast("Failed to copy link: " + err.message, "danger");
  });
}

export function populateRecipeViewDOM(data, recipeId = "") {
  if (!data) return;
  currentRecipeViewData = data;
  const targetId = recipeId || data.id || data.recipe_id || "recipe_001";

  try {

  // 1. Header Info
  const titleEl = document.getElementById("rvTitle");
  if (titleEl) titleEl.textContent = data.title || targetId;

    const pathEl = document.getElementById("rvPathLabel");
    if (pathEl) pathEl.textContent = `/recipes/${recipeId}`;
    
    const slugLink = document.getElementById("rvSlugLink");
    if (slugLink) {
      slugLink.textContent = `/${data.slug || recipeId}`;
      slugLink.href = `https://weeknightbiteapp.com/r/${encodeURIComponent(data.slug || recipeId)}`;
    }

    const pubEl = document.getElementById("rvPublishedAt");
    if (pubEl) pubEl.textContent = `Published: ${formatRecipeDate(data.published_at) || 'N/A'}`;

    // Raw JSON text & Copy
    const jsonPre = document.getElementById("rvJsonPre");
    if (jsonPre) jsonPre.textContent = JSON.stringify(data, null, 2);

    const copyJsonBtn = document.getElementById("rvCopyJsonBtn");
    if (copyJsonBtn) {
      copyJsonBtn.onclick = () => {
        navigator.clipboard.writeText(JSON.stringify(data, null, 2));
        copyJsonBtn.textContent = "Copied";
        if (showToast) showToast(`Copied raw JSON to clipboard!`, "success");
        setTimeout(() => { copyJsonBtn.textContent = "Copy JSON"; }, 2000);
      };
    }

    const editBtn = document.getElementById("rvEditBtn");
    if (editBtn) {
      editBtn.onclick = () => window.crmApp.toggleUnifiedRecipeEdit(true); // through the edit guard in app.js
    }

    // Status Badge & Publish Toggle Button
    const rStatus = getRecipeStatus(data);
    const statusBadge = document.getElementById("rvStatusBadge");
    if (statusBadge) {
      const published = rStatus === "published";
      statusBadge.className = published ? BADGE.green : BADGE.yellow;
      statusBadge.innerHTML = `<span aria-hidden="true" class="${published ? BADGE_DOT.green : BADGE_DOT.yellow}"></span>${published ? "Published" : "Draft"}`;
    }

    const publishToggleBtn = document.getElementById("rvPublishToggleBtn");
    if (publishToggleBtn) {
      const published = rStatus === "published";
      publishToggleBtn.className = published ? BUTTON.secondary : BUTTON.success;
      publishToggleBtn.textContent = published ? "Move to draft" : "Publish recipe";
    }

    // 2. Hero Banner & Badges
    const heroImg = document.getElementById("rvHeroImage");
    if (heroImg) {
      heroImg.src = data.image_url || data.image || data.thumbnail_url || "https://images.unsplash.com/photo-1495521821757-a1efb6729352?w=300&auto=format&fit=crop&q=60";
    }

    const badgesBox = document.getElementById("rvBadgesContainer");
    if (badgesBox) {
      const sigBadge = data.is_signature
        ? `<img src="assets/signature-icon.svg" alt="Signature" title="Signature recipe (paywalled)" class="h-9 w-auto">`
        : `<span class="${BADGE.gray}">Free catalog</span>`;
      const secretBadge = data.is_secret ? `<span class="${BADGE.yellow}">Secret</span>` : ``;
      badgesBox.innerHTML = `${sigBadge} ${secretBadge}`.trim();
    }

    // 3. Planning Metrics
    const metrics = data.planning_metrics || {};
    const servings = Number(metrics.base_servings_adults || metrics.servings || data.base_servings_adults || data.servings || 2);
    const servingsEl = document.getElementById("rvServings");
    if (servingsEl) servingsEl.textContent = `${servings} portions`;
    
    const prepMinutes = Number(metrics.prep_time_minutes || data.prep_time_minutes || 0);
    const prepEl = document.getElementById("rvPrepTime");
    if (prepEl) prepEl.textContent = `${prepMinutes}m`;
    
    const cookMinutes = Number(metrics.cook_time_minutes || data.cook_time_minutes || 0);
    const cookEl = document.getElementById("rvCookTime");
    if (cookEl) cookEl.textContent = `${cookMinutes}m`;
    
    const tierEl = document.getElementById("rvTierDisplay");
    if (tierEl) {
      tierEl.innerHTML = data.is_signature
        ? `<span class="text-paprika-700 dark:text-paprika-300">Paid (Signature)</span>`
        : `<span class="text-green-700 dark:text-green-400">Free</span>`;
    }

    const visEl = document.getElementById("rvVisibilityDisplay");
    if (visEl) {
      visEl.innerHTML = data.is_secret
        ? `<span class="text-amber-700 dark:text-amber-400">Secret</span>`
        : `<span class="text-blue-700 dark:text-blue-400">Public</span>`;
    }

    const dishScore = Number(metrics.dish_load_score ?? data.dish_load_score ?? 1);
    const dishEl = document.getElementById("rvDishScore");
    if (dishEl) dishEl.textContent = `${dishScore} / 5`;

    // 4. Classification Metadata
    const classBox = document.getElementById("rvClassificationGrid");
    if (classBox) {
      const c = data.classification || {};

      const cuisineChip = renderCategoryChip(c.cuisine, activeCategoriesData);
      const proteinChip = renderCategoryChip(c.primary_protein, activeCategoriesData);
      const mealChip = renderCategoryChip(c.meal_type, activeCategoriesData);
      const methodChip = renderCategoryChip(c.method, activeCategoriesData);
      const carbChip = renderCategoryChip(c.carb_category, activeCategoriesData);

      const timeEffortList = Array.isArray(c.time_effort) ? c.time_effort : (c.effort ? [c.effort] : []);
      const timeEffortChips = timeEffortList.map(t => renderCategoryChip(t, activeCategoriesData)).join(' ') || '<span class="text-gray-400 dark:text-gray-500">None</span>';

      const lifestylesList = Array.isArray(c.goals_lifestyles)
        ? c.goals_lifestyles
        : (Array.isArray(c.lifestyle) ? c.lifestyle : (c.lifestyle ? [c.lifestyle] : []));
      const lifestylesChips = lifestylesList.map(l => renderCategoryChip(l, activeCategoriesData)).join(' ') || '<span class="text-gray-400 dark:text-gray-500">None</span>';

      const occasionsList = Array.isArray(c.occasions_seasons)
        ? c.occasions_seasons
        : (Array.isArray(c.occasion) ? c.occasion : (c.occasion ? [c.occasion] : []));
      const occasionsChips = occasionsList.map(o => renderCategoryChip(o, activeCategoriesData)).join(' ') || '<span class="text-gray-400 dark:text-gray-500">None</span>';

      const row = (label, chips) => `<div class="flex flex-wrap items-center gap-1.5"><span class="font-medium text-gray-700 dark:text-gray-300">${label}</span>${chips}</div>`;
      classBox.innerHTML = [
        row("Cuisine", cuisineChip),
        row("Protein", proteinChip),
        row("Meal", mealChip),
        row("Method", methodChip),
        c.carb_category ? row("Carb", carbChip) : "",
        row("Time and effort", timeEffortChips),
        row("Goals and lifestyle", lifestylesChips),
        row("Occasions and seasons", occasionsChips)
      ].join("");
    }

    // 5. Allergens & Diets
    const tagsBox = document.getElementById("rvAllergensDietsContainer");
    if (tagsBox) {
      const allergens = (data.allergens || data.allergen_flags || []).map(a => `<span class="${BADGE.red}" title="Allergen">${a}</span>`);
      const diets = (data.dietary_preferences || []).map(d => `<span class="${BADGE.green}" title="Diet">${d}</span>`);
      tagsBox.innerHTML = [...allergens, ...diets].join('') || `<span class="text-sm text-gray-500 dark:text-gray-400">None given</span>`;
    }

    // 6. Hardware Requirements
    const hardBox = document.getElementById("rvHardwareChipsContainer") || document.getElementById("rvHardwareList");
    if (hardBox) {
      const gear = Array.isArray(data.cookware)
        ? data.cookware
        : (data.kitchen_hardware_requirements?.cookware_required || data.kitchen_hardware_requirements?.cookware_and_utensils || data.cookware_required || []);
      hardBox.innerHTML = gear.length > 0
        ? gear.map(item => `<span class="${BADGE.gray}">${item}</span>`).join('')
        : `<span class="text-sm text-gray-500 dark:text-gray-400">Standard cookware</span>`;
    }

    // 7. Master Ingredients Table
    const ingTbody = document.getElementById("rvIngredientsTbody");
    if (ingTbody) {
      const ings = data.ingredients || [];
      if (ings.length === 0) {
        ingTbody.innerHTML = `<tr><td colspan="3" class="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">No ingredients listed.</td></tr>`;
      } else {
        ingTbody.innerHTML = ings.map(ing => {
          const uid = ing.ingredient_id || ing.uid || "";
          const amount = ing.amount ?? "";
          const unit = ing.unit ?? "";
          const isStaple = Boolean(ing.is_staple);
          const stapleBadge = isStaple ? `<span class="${BADGE.gray}" title="Pantry staple">Staple</span>` : "";
          const optBadge = ing.is_optional ? `<span class="${BADGE.yellow}">Optional</span>` : "";
          const prepNote = ing.prep_note
            ? `<div class="mt-0.5 text-xs text-gray-500 dark:text-gray-400">${ing.prep_note}</div>`
            : "";

          const catalogItem = (activeMasterIngredientsMap && activeMasterIngredientsMap[uid]) || (typeof window !== "undefined" && window.crmApp && window.crmApp.getMasterIngredient ? window.crmApp.getMasterIngredient(uid) : null);
          const isMissing = !catalogItem;

          if (!isMissing) {
            const name = catalogItem.display_name || catalogItem.name || ing.name_singular || ing.name || uid;
            const expectedUnit = (catalogItem.display_unit || "").toLowerCase().trim();
            const recipeUnit = (unit || "").toLowerCase().trim();

            const normalizeUnitForCompare = (u) => {
              if (!u) return "";
              if (u === "g" || u === "gram" || u === "grams") return "gram";
              if (u === "count" || u === "piece" || u === "pieces" || u === "pcs" || u === "whole") return "whole";
              if (u === "tbsp" || u === "tablespoon" || u === "tablespoons") return "tbsp";
              if (u === "tsp" || u === "teaspoon" || u === "teaspoons") return "tsp";
              if (u === "cup" || u === "cups") return "cup";
              if (u === "clove" || u === "cloves") return "clove";
              if (u === "can" || u === "cans") return "can";
              if (u === "head" || u === "heads") return "head";
              if (u === "bunch" || u === "bunches") return "bunch";
              if (u === "slice" || u === "slices") return "slice";
              if (u === "sprig" || u === "sprigs") return "sprig";
              if (u === "stalk" || u === "stalks") return "stalk";
              return u;
            };

            const normRecipeUnit = normalizeUnitForCompare(recipeUnit);
            const normExpectedUnit = normalizeUnitForCompare(expectedUnit);

            const isUnitMismatch = Boolean(normExpectedUnit && normRecipeUnit && normRecipeUnit !== normExpectedUnit);

            const unitBadge = isUnitMismatch
              ? `<div class="flex flex-wrap items-center gap-2">
                  <span class="${BADGE.yellow}" title="The recipe uses '${unit}', the catalog expects '${expectedUnit}'">${solidIcon("exclamation-triangle", "size-3.5")}${unit} <span class="font-normal opacity-80">(catalog: ${expectedUnit})</span></span>
                  <button type="button" onclick="event.stopPropagation(); window.crmApp.quickFixIngredientUnit(${jsArg(recipeId)}, ${jsArg(uid)}, ${jsArg(expectedUnit)})" title="Change the unit to '${expectedUnit}' in Firestore" class="${BUTTON.small}">Use ${expectedUnit}</button>
                </div>`
              : `<span class="${BADGE.gray}">${unit}</span>`;

            return `
              <tr onclick="window.crmApp.openIngredientInspector(${jsArg(uid)})" title="Open ${name} in the ingredient inspector" class="cursor-pointer hover:bg-gray-50 dark:hover:bg-white/5">
                <td class="py-3 pr-3 pl-4 text-sm">
                  <div class="flex flex-wrap items-center gap-1.5"><span class="font-medium text-gray-900 dark:text-white">${name}</span>${stapleBadge}${optBadge}</div>
                  ${prepNote}
                </td>
                <td class="px-3 py-3 text-sm font-semibold whitespace-nowrap text-gray-900 dark:text-white">${amount}</td>
                <td class="py-3 pr-4 pl-3 text-sm">${unitBadge}</td>
              </tr>
            `;
          } else {
            const name = ing.name_singular || ing.name || uid;
            return `
              <tr onclick="window.crmApp.openAddMissingIngredientForm(${jsArg(uid)})" title="'${name}' is not in the master ingredients" class="cursor-pointer bg-yellow-50/60 hover:bg-yellow-50 dark:bg-yellow-400/5 dark:hover:bg-yellow-400/10">
                <td class="py-3 pr-3 pl-4 text-sm">
                  <div class="flex flex-wrap items-center gap-1.5"><span class="font-medium text-gray-900 dark:text-white">${name}</span>${stapleBadge}${optBadge}<span class="${BADGE.yellow}">${solidIcon("exclamation-triangle", "size-3.5")}Missing</span></div>
                  ${prepNote}
                </td>
                <td class="px-3 py-3 text-sm font-semibold whitespace-nowrap text-gray-900 dark:text-white">${amount}</td>
                <td class="py-3 pr-4 pl-3 text-sm"><span class="${BADGE.gray}">${unit}</span></td>
              </tr>
            `;
          }
        }).join('');
      }
    }

    // 8. Instructions Timelines
    renderRecipeViewInstructions();

    const mainContent = document.getElementById("recipeViewMainContent");
    if (mainContent) mainContent.style.display = "block";
  } catch (err) {
    console.error("Error populating recipe view DOM:", err);
  }
}

let activeCatalogRecipes = {};
let recipeStatusFilter = "all"; // "all" | "published" | "draft"
let selectedRecipeIds = new Set();
let visibleRecipeIds = [];

export function getSelectedRecipeIds() {
  return Array.from(selectedRecipeIds);
}

export function toggleRecipeSelection(id, isSelected) {
  if (isSelected) {
    selectedRecipeIds.add(id);
  } else {
    selectedRecipeIds.delete(id);
  }
  updateBulkActionsToolbar();
}

export function toggleSelectAllRecipes(selectAll) {
  if (selectAll) {
    visibleRecipeIds.forEach((id) => selectedRecipeIds.add(id));
  } else {
    visibleRecipeIds.forEach((id) => selectedRecipeIds.delete(id));
  }
  renderRecipeTable(activeCatalogRecipes);
}

export function clearRecipeSelection() {
  selectedRecipeIds.clear();
  renderRecipeTable(activeCatalogRecipes);
}

export function updateBulkActionsToolbar() {
  const toolbar = document.getElementById("recipeBulkActionsToolbar");
  const countEl = document.getElementById("bulkSelectionCount");
  const headerCheckbox = document.getElementById("selectAllRecipesCheckbox");

  const count = selectedRecipeIds.size;
  if (countEl) {
    countEl.textContent = `${count} selected`;
  }

  if (toolbar) {
    toolbar.hidden = count === 0;
  }

  if (headerCheckbox) {
    if (visibleRecipeIds.length === 0) {
      headerCheckbox.checked = false;
      headerCheckbox.indeterminate = false;
    } else {
      const visibleSelectedCount = visibleRecipeIds.filter((id) => selectedRecipeIds.has(id)).length;
      if (visibleSelectedCount === 0) {
        headerCheckbox.checked = false;
        headerCheckbox.indeterminate = false;
      } else if (visibleSelectedCount === visibleRecipeIds.length) {
        headerCheckbox.checked = true;
        headerCheckbox.indeterminate = false;
      } else {
        headerCheckbox.checked = false;
        headerCheckbox.indeterminate = true;
      }
    }
  }
}

export function updateActiveCatalogRecipes(catalogRecipes = {}) {
  activeCatalogRecipes = catalogRecipes;
}

export function getRecipeStatus(recipe) {
  if (!recipe) return "draft";
  if (recipe.status === "published") return "published";
  if (recipe.status === "draft") return "draft";
  if (recipe.published_at) return "published";
  return "draft";
}

export async function toggleRecipePublishStatus(db, setDoc, doc, showToast = null, dismissToast = null, getDoc = null, auth = null) {
  const hash = window.location.hash.replace("#", "").trim();
  let recipeId = "";
  if (hash.includes("?id=")) {
    recipeId = hash.split("?id=")[1].split("&")[0];
  } else if (hash.includes("id=")) {
    recipeId = new URLSearchParams(hash.split("?")[1] || "").get("id") || "";
  } else {
    recipeId = new URLSearchParams(window.location.search).get("id") || "";
  }
  recipeId = decodeURIComponent(recipeId || "").trim();

  if (!recipeId && currentRecipeViewData) {
    recipeId = currentRecipeViewData.recipe_id || currentRecipeViewData.id || "";
  }

  if (!recipeId || !currentRecipeViewData) {
    if (showToast) showToast("⚠️ No active recipe document found to toggle publish status.", "warning");
    return;
  }

  const currentStatus = getRecipeStatus(currentRecipeViewData);
  const willPublish = currentStatus !== "published";
  const newStatus = willPublish ? "published" : "draft";
  const nextStatus = newStatus;

  const updatePayload = {
    status: newStatus,
    updated_at: new Date().toISOString()
  };
  if (willPublish) {
    updatePayload.published_at = new Date().toISOString();
  }

  let toast = null;
  if (showToast) toast = showToast(`⏳ ${willPublish ? "Publishing" : "Moving to draft"} '${currentRecipeViewData.title || recipeId}'...`, "processing");

  try {
    const docRef = doc(db, "recipes", recipeId);
    await setDoc(docRef, updatePayload, { merge: true });

    // Non-blocking audit log recording
    try {
      if (getDoc) {
        await recordRecipeAuditLog({
          db,
          setDoc,
          doc,
          getDoc,
          recipeId,
          action: ACTION_TYPES.STATUS_TRANSITION,
          summary: `Changed status from '${currentStatus}' to '${nextStatus}'`,
          details: {
            old_status: currentStatus,
            new_status: nextStatus
          },
          currentUser: auth?.currentUser || (typeof auth === "object" && auth?.uid ? auth : null) || (typeof currentUser !== "undefined" ? currentUser : null)
        });
      }
    } catch (auditErr) {
      console.warn("⚠️ Non-blocking: Failed to record audit log for status transition:", auditErr);
    }

    if (dismissToast && toast) dismissToast(toast);

    // Update in-memory state
    currentRecipeViewData.status = newStatus;
    if (willPublish) {
      currentRecipeViewData.published_at = updatePayload.published_at;
    }
    if (activeCatalogRecipes && activeCatalogRecipes[recipeId]) {
      activeCatalogRecipes[recipeId].status = newStatus;
      if (willPublish) {
        activeCatalogRecipes[recipeId].published_at = updatePayload.published_at;
      }
    }

    // Refresh DOM badge, button, and other data
    populateRecipeViewDOM(currentRecipeViewData, recipeId);

    // Refresh audit log timeline in viewer DOM
    if (getDoc) {
      await refreshRecipeAuditLog(db, doc, getDoc, recipeId);
    }

    if (showToast) {
      if (willPublish) {
        showToast(`✓ Published recipe '${currentRecipeViewData.title || recipeId}'!`, "success");
      } else {
        showToast(`✓ Moved '${currentRecipeViewData.title || recipeId}' to draft.`, "info");
      }
    }
  } catch (err) {
    if (dismissToast && toast) dismissToast(toast);
    console.error("❌ Failed to toggle recipe publish status:", err);
    if (showToast) showToast(`❌ Error updating status: ${err.message}`, "danger");
  }
}

export function setRecipeStatusFilter(filter) {
  recipeStatusFilter = filter || "all";
  document.querySelectorAll("#sectionRecipes [data-status-filter]").forEach((btn) => {
    btn.setAttribute("aria-pressed", String(btn.getAttribute("data-status-filter") === recipeStatusFilter));
  });
  renderRecipeTable(activeCatalogRecipes);
}

let activeSortColumn = "published_at";
let activeSortDirection = "desc";

export function updateSortHeaderUI() {
  const columns = ["title", "status", "is_signature", "total_time", "rating", "published_at"];
  columns.forEach((col) => {
    // The chevron points down for descending; data-active shows it, asc turns it over.
    const iconEl = document.getElementById(`sortIcon_${col}`);
    if (iconEl) {
      const active = activeSortColumn === col;
      iconEl.toggleAttribute("data-active", active);
      iconEl.dataset.sortDir = active ? activeSortDirection : "";
    }
  });

  const select = document.getElementById("recipeSortSelect");
  if (select) {
    const combined = `${activeSortColumn}_${activeSortDirection}`;
    if (select.querySelector(`option[value="${combined}"]`)) {
      select.value = combined;
    }
  }
}

export function handleColumnSort(columnKey) {
  if (activeSortColumn === columnKey) {
    activeSortDirection = activeSortDirection === "asc" ? "desc" : "asc";
  } else {
    activeSortColumn = columnKey;
    activeSortDirection = (columnKey === "title" || columnKey === "is_signature" || columnKey === "status") ? "asc" : "desc";
  }
  updateSortHeaderUI();
  renderRecipeTable(activeCatalogRecipes);
}

export function onRecipeSortDropdownChange() {
  const val = document.getElementById("recipeSortSelect")?.value || "published_at_desc";
  if (val.includes("_")) {
    const parts = val.split("_");
    const dir = parts.pop();
    const col = parts.join("_");
    if (dir === "asc" || dir === "desc") {
      activeSortDirection = dir;
      activeSortColumn = col;
    }
  }
  updateSortHeaderUI();
  renderRecipeTable(activeCatalogRecipes);
}

export function onRecipeSearchInput() {
  renderRecipeTable(activeCatalogRecipes);
}

function formatRecipeDate(rawDate) {
  if (!rawDate) return "";
  let dateObj = null;
  if (typeof rawDate === "object" && rawDate.seconds) {
    dateObj = new Date(rawDate.seconds * 1000);
  } else if (typeof rawDate === "object" && typeof rawDate.toDate === "function") {
    dateObj = rawDate.toDate();
  } else if (typeof rawDate === "string" || typeof rawDate === "number") {
    dateObj = new Date(rawDate);
  }
  if (!dateObj || isNaN(dateObj.getTime())) return "";
  return dateObj.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" });
}

export function renderRecipeTable(catalogRecipes = {}) {
  const tbody = document.getElementById("recipeTableBody");
  if (!tbody) return;

  const recipesMap = (catalogRecipes && Object.keys(catalogRecipes).length > 0)
    ? catalogRecipes
    : activeCatalogRecipes;

  if (catalogRecipes && Object.keys(catalogRecipes).length > 0) {
    activeCatalogRecipes = catalogRecipes;
  }

  // Update status filter counter counts
  let countAll = 0;
  let countPub = 0;
  let countDraft = 0;
  Object.values(recipesMap).forEach((r) => {
    const st = getRecipeStatus(r || {});
    countAll++;
    if (st === "published") countPub++;
    else if (st === "draft") countDraft++;
  });
  const elAll = document.getElementById("countAllRecipes");
  if (elAll) elAll.textContent = countAll;
  const elPub = document.getElementById("countPubRecipes");
  if (elPub) elPub.textContent = countPub;
  const elDraft = document.getElementById("countDraftRecipes");
  if (elDraft) elDraft.textContent = countDraft;

  const searchVal = (document.getElementById("recipeSearchInput")?.value || "").toLowerCase().trim();
  const signatureFilter = document.getElementById("recipeSignatureFilter")?.value || "all";
  const typeFilter = document.getElementById("recipeTypeFilter")?.value || "all";

  const getRecipeTimestamp = (r) => {
    const raw = r.published_at || r.created_at || r.updated_at;
    if (!raw) return 0;
    if (typeof raw === "object" && raw.seconds) return raw.seconds * 1000;
    if (typeof raw === "number") return raw;
    const parsed = Date.parse(raw);
    return isNaN(parsed) ? 0 : parsed;
  };

  const recipeKeys = Object.keys(recipesMap).filter((id) => {
    const recipe = recipesMap[id] || {};
    const title = (recipe.title || "").toLowerCase();
    const slug = (recipe.slug || "").toLowerCase();
    const matchesSearch = !searchVal || title.includes(searchVal) || slug.includes(searchVal) || id.toLowerCase().includes(searchVal);

    const isSig = recipe.is_signature === true;
    const matchesSig = signatureFilter === "all" || (signatureFilter === "signature" && isSig) || (signatureFilter === "free" && !isSig);
    const matchesType = typeFilter === "all" || recipe.recipe_type === typeFilter;

    const rStatus = getRecipeStatus(recipe);
    const matchesStatus = recipeStatusFilter === "all" || rStatus === recipeStatusFilter;

    return matchesSearch && matchesSig && matchesType && matchesStatus;
  });

  recipeKeys.sort((aId, bId) => {
    const rA = recipesMap[aId] || {};
    const rB = recipesMap[bId] || {};

    let valA, valB;
    if (activeSortColumn === "title") {
      valA = (rA.title || aId).toLowerCase();
      valB = (rB.title || bId).toLowerCase();
      return activeSortDirection === "asc" ? valA.localeCompare(valB) : valB.localeCompare(valA);
    } else if (activeSortColumn === "status") {
      valA = getRecipeStatus(rA);
      valB = getRecipeStatus(rB);
      return activeSortDirection === "asc" ? valA.localeCompare(valB) : valB.localeCompare(valA);
    } else if (activeSortColumn === "is_signature") {
      valA = rA.is_signature ? 1 : 0;
      valB = rB.is_signature ? 1 : 0;
    } else if (activeSortColumn === "total_time") {
      const mA = rA.planning_metrics || {};
      const mB = rB.planning_metrics || {};
      const prepA = Number(mA.prep_time_minutes || rA.prep_time_minutes || 0);
      const cookA = Number(mA.cook_time_minutes || rA.cook_time_minutes || 0);
      valA = Number(mA.total_time_minutes || rA.total_time_minutes || (prepA + cookA) || 0);

      const prepB = Number(mB.prep_time_minutes || rB.prep_time_minutes || 0);
      const cookB = Number(mB.cook_time_minutes || rB.cook_time_minutes || 0);
      valB = Number(mB.total_time_minutes || rB.total_time_minutes || (prepB + cookB) || 0);
    } else if (activeSortColumn === "rating") {
      valA = Number(rA.average_rating ?? rA.rating ?? 0);
      valB = Number(rB.average_rating ?? rB.rating ?? 0);
    } else { // published_at
      valA = getRecipeTimestamp(rA);
      valB = getRecipeTimestamp(rB);
    }

    if (valA < valB) return activeSortDirection === "asc" ? -1 : 1;
    if (valA > valB) return activeSortDirection === "asc" ? 1 : -1;
    return 0;
  });

  visibleRecipeIds = recipeKeys;

  if (recipeKeys.length === 0) {
    tbody.innerHTML = `<tr><td colspan="9" class="px-6 py-12 text-center text-sm text-gray-500 dark:text-gray-400">No recipes match the filters.</td></tr>`;
    updateBulkActionsToolbar();
    return;
  }

  const esc = (value) => String(value ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);
  const fallbackThumb = "https://images.unsplash.com/photo-1495521821757-a1efb6729352?w=100&auto=format&fit=crop&q=60";

  tbody.innerHTML = recipeKeys.map((id) => {
    const r = recipesMap[id] || {};
    const isSelected = selectedRecipeIds.has(id);
    const imgUrl = r.thumbnail_url || r.image_url || fallbackThumb;
    const m = r.planning_metrics || {};
    const prepTime = Number(m.prep_time_minutes || r.prep_time_minutes || 0);
    const cookTime = Number(m.cook_time_minutes || r.cook_time_minutes || 0);
    const totalTime = Number(m.total_time_minutes || r.total_time_minutes || (prepTime + cookTime) || 0);
    const publishedStr = formatRecipeDate(r.published_at || r.created_at || r.updated_at);
    const hasRating = r.average_rating != null || r.rating != null;
    const avgRating = hasRating ? Number(r.average_rating ?? r.rating) : null;
    const ratingCount = r.rating_count ?? r.review_count ?? r.reviews_count ?? null;

    const ratingCell = (avgRating !== null && !isNaN(avgRating))
      ? `<div class="flex items-center gap-x-1 font-medium text-gray-900 dark:text-white">${solidIcon("star", "size-4 text-amber-500")}${avgRating.toFixed(1)}</div>${ratingCount !== null ? `<div class="mt-0.5 text-xs text-gray-500 dark:text-gray-400">${ratingCount} review${ratingCount === 1 ? "" : "s"}</div>` : ""}`
      : `<span class="text-gray-400 dark:text-gray-500">—</span>`;

    const rStatus = getRecipeStatus(r);
    const statusBadge = rStatus === "published"
      ? `<span class="${BADGE.green}"><span aria-hidden="true" class="${BADGE_DOT.green}"></span>Published</span>`
      : `<span class="${BADGE.yellow}"><span aria-hidden="true" class="${BADGE_DOT.yellow}"></span>Draft</span>`;

    const sigBadge = r.is_signature
      ? `<img src="assets/signature-icon.svg" alt="Signature" title="Signature recipe (paywalled)" class="h-6 w-auto">`
      : `<span class="${BADGE.gray}">Free</span>`;

    const sourceUrl = r.source_url || r.full_url || r.source?.url || null;
    let sourceDomain = r.source_domain || r.source?.domain || "";
    if (!sourceDomain && sourceUrl) {
      try {
        sourceDomain = new URL(sourceUrl).hostname.replace(/^www\./, "");
      } catch (_) {}
    }
    const sourcePill = sourceUrl
      ? `<a href="${esc(sourceUrl)}" target="_blank" rel="noopener noreferrer" onclick="event.stopPropagation()" title="Source: ${esc(sourceUrl)}" class="${BADGE.blue} hover:underline">${solidIcon("link", "size-3")}${esc(sourceDomain || "Source")}</a>`
      : "";

    return `
      <tr onclick="window.crmApp.viewRecipe(${jsArg(id)})" title="Open the recipe" class="cursor-pointer hover:bg-gray-50 has-checked:bg-paprika-50/60 dark:hover:bg-white/5 dark:has-checked:bg-white/5">
        <td class="py-4 pr-3 pl-4 sm:pl-6" onclick="event.stopPropagation()">
          ${checkbox(`aria-label="Select ${esc(r.title || id)}" data-recipe-id="${esc(id)}" onchange="window.crmApp.toggleRecipeSelection(${jsArg(id)}, this.checked)"${isSelected ? " checked" : ""}`)}
        </td>
        <td class="px-3 py-4">
          <img src="${esc(imgUrl)}" alt="" loading="lazy" onerror="this.onerror=null; this.src='${fallbackThumb}'" class="relative z-[1] size-10 max-w-none cursor-zoom-in rounded-lg bg-cream object-cover outline -outline-offset-1 outline-black/5 transition duration-300 ease-[cubic-bezier(0.175,0.885,0.32,1.275)] hover:z-50 hover:scale-[3.5] hover:shadow-xl dark:bg-gray-800 dark:outline-white/10">
        </td>
        <td class="px-3 py-4 text-sm">
          <div class="font-medium text-gray-900 dark:text-white">${esc(r.title || id)}</div>
          <div class="mt-1 flex flex-wrap items-center gap-2">
            <a href="https://weeknightbiteapp.com/r/${encodeURIComponent(r.slug || id)}" target="_blank" rel="noopener noreferrer" onclick="event.stopPropagation()" title="Open the public recipe page" class="inline-flex items-center gap-x-1 font-mono text-xs text-paprika-700 hover:underline dark:text-paprika-300">/${esc(r.slug || id)}${solidIcon("arrow-top-right-on-square", "size-3")}</a>
            ${sourcePill}
          </div>
        </td>
        <td class="px-3 py-4 text-sm whitespace-nowrap">${statusBadge}</td>
        <td class="px-3 py-4 text-sm whitespace-nowrap">${sigBadge}</td>
        <td class="px-3 py-4 text-sm whitespace-nowrap">
          <div class="font-medium text-gray-900 dark:text-white">${totalTime} min</div>
          <div class="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Prep ${prepTime} · Cook ${cookTime}</div>
        </td>
        <td class="px-3 py-4 text-sm whitespace-nowrap">${ratingCell}</td>
        <td class="px-3 py-4 text-sm whitespace-nowrap text-gray-500 dark:text-gray-400">${publishedStr || "—"}</td>
        <td class="py-4 pr-4 pl-3 text-right text-sm whitespace-nowrap sm:pr-6" onclick="event.stopPropagation()">
          <div class="flex justify-end gap-x-2">
            <button type="button" onclick="window.crmApp.printRecipeJson(${jsArg(id)})" title="Copy the raw JSON of ${esc(r.title || id)}" class="${BUTTON.small}">JSON</button>
            <button type="button" onclick="window.crmApp.editRecipe(${jsArg(id)})" class="${BUTTON.small}">Edit</button>
            <button type="button" onclick="window.crmApp.deleteRecipe(${jsArg(id)})" class="${BUTTON.smallDanger}">Delete</button>
          </div>
        </td>
      </tr>
    `;
  }).join("");

  updateBulkActionsToolbar();
}

export function printRecipeJson(id = null, catalogRecipes = {}, showToast = null) {
  const recipesMap = (catalogRecipes && Object.keys(catalogRecipes).length > 0)
    ? catalogRecipes
    : activeCatalogRecipes;

  const targetId = id || document.getElementById("formRecipeId")?.value.trim() || document.getElementById("formRecipeSlug")?.value.trim();
  const recipeData = recipesMap[targetId];

  if (!recipeData) {
    const errorMsg = `❌ Recipe '${targetId || 'unknown'}' not found in catalog.`;
    if (showToast) showToast(errorMsg, "danger");
    console.error(errorMsg);
    return;
  }

  const jsonString = JSON.stringify(recipeData, null, 2);
  console.log(`📋 [Recipe JSON] (${targetId}):\n`, jsonString);

  if (typeof navigator !== "undefined" && navigator.clipboard) {
    navigator.clipboard.writeText(jsonString).then(() => {
      if (showToast) showToast(`📋 Printed & copied JSON for '${targetId}' to console & clipboard!`, "success");
    }).catch(() => {
      if (showToast) showToast(`📋 Printed JSON for '${targetId}' to browser console!`, "success");
    });
  } else {
    if (showToast) showToast(`📋 Printed JSON for '${targetId}' to browser console!`, "success");
  }
}

let importProgressInterval = null;

export function openImportRecipeModal() {
  const modal = document.getElementById("importRecipeModal");
  if (!modal) return;

  const urlInput = document.getElementById("importRecipeUrlInput");
  const textInput = document.getElementById("importRecipeTextInput");
  if (urlInput) urlInput.value = "";
  if (textInput) textInput.value = "";

  switchImportTab("url");

  const formView = document.getElementById("importRecipeFormView");
  const loadingView = document.getElementById("importRecipeLoadingView");
  const footer = document.getElementById("importRecipeFooter");

  if (formView) formView.style.display = "block";
  if (loadingView) loadingView.style.display = "none";
  if (footer) footer.style.display = "flex";

  const notifPrompt = document.getElementById("importNotifPermPrompt");
  if (notifPrompt) {
    notifPrompt.style.display = getNotificationPermission() === "default" ? "flex" : "none";
  }

  showDialog("importRecipeModal");

  if (urlInput) {
    urlInput.focus();
    setTimeout(() => {
      urlInput.focus();
      urlInput.select();
    }, 50);
  }
}

export function switchImportTab(mode) {
  const tabUrl = document.getElementById("tabImportUrl");
  const tabText = document.getElementById("tabImportText");
  const urlBox = document.getElementById("importUrlBox");
  const textBox = document.getElementById("importTextBox");
  const urlInput = document.getElementById("importRecipeUrlInput");
  const textInput = document.getElementById("importRecipeTextInput");

  tabUrl?.setAttribute("aria-selected", String(mode === "url"));
  tabText?.setAttribute("aria-selected", String(mode !== "url"));

  if (mode === "url") {
    if (urlBox) urlBox.style.display = "block";
    if (textBox) textBox.style.display = "none";
    if (urlInput) {
      urlInput.focus();
      setTimeout(() => {
        urlInput.focus();
        urlInput.select();
      }, 50);
    }
  } else {
    if (urlBox) urlBox.style.display = "none";
    if (textBox) textBox.style.display = "block";
    if (textInput) {
      textInput.focus();
      setTimeout(() => {
        textInput.focus();
        textInput.select();
      }, 50);
    }
  }
}

export async function extractRecipeFromUrlClientSide(url) {
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const htmlText = await res.text();

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlText, "text/html");
    const scriptTags = doc.querySelectorAll('script[type="application/ld+json"]');

    let recipeObj = null;

    for (const tag of scriptTags) {
      try {
        const json = JSON.parse(tag.textContent);
        const candidates = Array.isArray(json) ? json : (json["@graph"] || [json]);
        const found = candidates.find((item) => item && (item["@type"] === "Recipe" || (Array.isArray(item["@type"]) && item["@type"].includes("Recipe"))));
        if (found) {
          recipeObj = found;
          break;
        }
      } catch (e) {
        // Skip invalid JSON block
      }
    }

    if (recipeObj) {
      const name = recipeObj.name || recipeObj.headline || "";
      const description = recipeObj.description || "";
      const ingredients = Array.isArray(recipeObj.recipeIngredient) ? recipeObj.recipeIngredient.join("\n") : "";

      let instructionsText = "";
      if (Array.isArray(recipeObj.recipeInstructions)) {
        instructionsText = recipeObj.recipeInstructions.map((step) => {
          if (typeof step === "string") return step;
          if (step && step.text) return step.text;
          if (step && step.name) return step.name;
          return "";
        }).filter(Boolean).join("\n");
      } else if (typeof recipeObj.recipeInstructions === "string") {
        instructionsText = recipeObj.recipeInstructions;
      }

      const prepTime = recipeObj.prepTime || "";
      const cookTime = recipeObj.cookTime || "";

      let formattedText = `TITLE: ${name}\n`;
      if (description) formattedText += `DESCRIPTION: ${description}\n`;
      if (prepTime) formattedText += `PREP TIME: ${prepTime}\n`;
      if (cookTime) formattedText += `COOK TIME: ${cookTime}\n`;
      formattedText += `\nINGREDIENTS:\n${ingredients}\n\nINSTRUCTIONS:\n${instructionsText}`;

      return formattedText;
    }
  } catch (err) {
    console.warn("⚠️ Client-side schema.org extraction notice:", err.message);
  }

  return `RECIPE LINK: ${url}`;
}

export async function submitImportRecipe(functions, httpsCallable, showToast = null, dismissToast = null) {
  const urlInput = document.getElementById("importRecipeUrlInput");
  const textInput = document.getElementById("importRecipeTextInput");
  const urlBox = document.getElementById("importUrlBox");

  const isUrlMode = urlBox && urlBox.style.display !== "none";
  let payloadText = "";
  let sourceOrigin = null;

  if (isUrlMode) {
    const rawUrl = urlInput?.value?.trim() || "";
    if (!rawUrl) {
      if (showToast) showToast("⚠️ Please enter a recipe web link URL.", "warning");
      return;
    }
    let domain = "";
    try {
      domain = new URL(rawUrl).hostname.replace(/^www\./, "");
    } catch (_) {}
    sourceOrigin = {
      type: "url",
      url: rawUrl,
      domain: domain,
      captured_at: new Date().toISOString()
    };
    payloadText = await extractRecipeFromUrlClientSide(rawUrl);
  } else {
    payloadText = textInput?.value?.trim() || "";
    if (!payloadText) {
      if (showToast) showToast("⚠️ Please paste recipe text.", "warning");
      return;
    }
    sourceOrigin = {
      type: "text",
      url: null,
      domain: null,
      captured_at: new Date().toISOString()
    };
  }

  const formView = document.getElementById("importRecipeFormView");
  const loadingView = document.getElementById("importRecipeLoadingView");
  const footer = document.getElementById("importRecipeFooter");
  const statusText = document.getElementById("importProgressStatus");
  const progressBar = document.getElementById("importProgressBar");

  // Pre-warm audio context and request notification permission during user gesture
  initAudioContext();
  requestNotificationPermission().catch(() => {});

  if (formView) formView.style.display = "none";
  if (loadingView) loadingView.style.display = "block";
  if (footer) footer.style.display = "none";

  let progressPct = 15;
  if (progressBar) progressBar.style.width = "15%";
  if (statusText) statusText.textContent = "Extracting the recipe's structure and parsing the ingredients…";

  const statusMessages = [
    { pct: 35, msg: "🏷️ Classifying dietary tags, effort, and matching master ingredients..." },
    { pct: 65, msg: "⚡ Building Fastest & Dish Saver timelines concurrently..." },
    { pct: 88, msg: "✨ Compiling the recipe..." }
  ];

  let stepIdx = 0;
  if (importProgressInterval) clearInterval(importProgressInterval);
  importProgressInterval = setInterval(() => {
    if (stepIdx < statusMessages.length) {
      if (statusText) statusText.textContent = statusMessages[stepIdx].msg;
      if (progressBar) progressBar.style.width = `${statusMessages[stepIdx].pct}%`;
      stepIdx++;
    } else {
      if (progressBar && progressPct < 96) {
        progressPct += 2;
        progressBar.style.width = `${progressPct}%`;
      }
    }
  }, 10000);

  try {
    const importFn = httpsCallable(functions, "importRecipeFromText");
    const result = await importFn({ recipeText: payloadText });

    if (importProgressInterval) clearInterval(importProgressInterval);
    if (progressBar) progressBar.style.width = "100%";

    const generatedRecipe = result.data?.recipe;
    if (!generatedRecipe) throw new Error("No recipe data returned from AI engine.");

    generatedRecipe.source = sourceOrigin;

    const tokenUsage = result.data?.token_usage || {};
    const costUsd = tokenUsage.estimated_cost_usd ?? tokenUsage.total?.estimated_cost_usd;
    const totalTokens = tokenUsage.total_tokens ?? tokenUsage.total?.total_tokens;
    const inTokens = tokenUsage.input_tokens ?? tokenUsage.total?.input_tokens;
    const outTokens = tokenUsage.output_tokens ?? tokenUsage.total?.output_tokens;

    let costSummary = "";
    if (costUsd != null && totalTokens != null) {
      costSummary = `💰 Cost: ~$${Number(costUsd).toFixed(4)} (${totalTokens.toLocaleString()} tokens)`;
    }

    if (showToast) {
      const toastMsg = costSummary 
        ? `✓ AI Recipe generated successfully! (${costSummary})`
        : "✓ AI Recipe generated successfully! Review & modify below.";
      showToast(toastMsg, "success");
    }

    // Trigger audio chime, desktop notification, and tab alert if tab is backgrounded
    notifyRecipeReady(generatedRecipe.title || generatedRecipe.recipe_title);

    hideDialog("importRecipeModal");

    if (formView) formView.style.display = "block";
    if (loadingView) loadingView.style.display = "none";
    if (footer) footer.style.display = "flex";

    currentNewRecipeData = generatedRecipe;
    await renderRecipeNewSection();
    populateRecipeNewFromAiResponse(generatedRecipe, result.data);

  } catch (err) {
    if (importProgressInterval) clearInterval(importProgressInterval);
    console.error("❌ AI Recipe Import Error:", err);
    if (showToast) showToast(`❌ Failed to generate recipe: ${err.message}`, "danger");

    notifyRecipeError(err.message);

    if (formView) formView.style.display = "block";
    if (loadingView) loadingView.style.display = "none";
    if (footer) footer.style.display = "flex";
  }
}

export function openRecipeEditor(id = null, catalogRecipes = {}, masterIngredients = {}) {
  if (!id) {
    renderRecipeNewSection();
    return;
  }
  const modal = document.getElementById("recipeModal");
  if (!modal) return;

  const form = document.getElementById("recipeForm");
  form.reset();

  const ingContainer = document.getElementById("ingredientsListContainer");
  const instContainer = document.getElementById("instructionsListContainer");
  if (ingContainer) ingContainer.innerHTML = "";
  if (instContainer) instContainer.innerHTML = "";

  if (id && catalogRecipes[id]) {
    const r = catalogRecipes[id];
    document.getElementById("recipeModalTitle").textContent = `Edit recipe: ${r.title || id}`;
    document.getElementById("formRecipeId").value = id;
    document.getElementById("formTitle").value = r.title || "";
    document.getElementById("formSlug").value = r.slug || id;
    const formStatus = document.getElementById("formStatus");
    if (formStatus) formStatus.value = getRecipeStatus(r);
    document.getElementById("formRecipeType").value = r.recipe_type || "global";
    const thumbInput = document.getElementById("formThumbnailUrl");
    thumbInput.value = r.image_url || r.thumbnail_url || "";
    thumbInput.dataset.imageUrl = r.image_url || "";
    thumbInput.dataset.thumbnailUrl = r.thumbnail_url || "";
    document.getElementById("formPrepTime").value = r.prep_time_minutes || 10;
    document.getElementById("formCookTime").value = r.cook_time_minutes || 20;
    document.getElementById("formServings").value = r.servings || 4;
    document.getElementById("formAverageRating").value = r.average_rating || 4.8;
    document.getElementById("formAllergens").value = (r.allergen_flags || r.allergens || []).join(", ");
    document.getElementById("formDietaryPreferences").value = (r.dietary_preferences || []).join(", ");
    const formSourceUrl = document.getElementById("formSourceUrl");
    if (formSourceUrl) {
      formSourceUrl.value = r.source_url || r.full_url || r.source?.url || "";
      const targetRecipeId = r.recipe_id || r.id;
      if (targetRecipeId && typeof getDoc === "function" && typeof doc === "function") {
        fetchRecipeAdminMetadata({ db, doc, getDoc, recipeId: targetRecipeId }).then((meta) => {
          if (meta && meta.source && meta.source.url) {
            formSourceUrl.value = meta.source.url;
            if (typeof activeCatalogRecipes !== "undefined" && activeCatalogRecipes[targetRecipeId]) {
              activeCatalogRecipes[targetRecipeId].source_url = meta.source.url;
              activeCatalogRecipes[targetRecipeId].source_domain = meta.source.domain || "";
            }
          }
        }).catch((err) => console.warn("Failed to fetch admin metadata for editor source URL:", err));
      }
    }

    // Populated Dynamic Ingredients
    const ings = r.ingredients || [];
    if (ings.length > 0) {
      ings.forEach((ing) => addIngredientRow(masterIngredients, ing));
    } else {
      addIngredientRow(masterIngredients);
    }

    // Populated Dynamic Instructions
    const insts = r.instructions || r.steps || [];
    if (insts.length > 0) {
      insts.forEach((inst) => {
        const text = typeof inst === "string" ? inst : inst.text || inst.instruction || "";
        addInstructionRow(text);
      });
    } else {
      addInstructionRow();
    }
  } else {
    document.getElementById("recipeModalTitle").textContent = "Create recipe";
    document.getElementById("formRecipeId").value = "";
    const formStatus = document.getElementById("formStatus");
    if (formStatus) formStatus.value = "draft";
    const formSourceUrl = document.getElementById("formSourceUrl");
    if (formSourceUrl) formSourceUrl.value = "";
    addIngredientRow(masterIngredients);
    addInstructionRow();
  }

  showDialog("recipeModal");
}

export function addIngredientRow(masterIngredients = {}, data = {}) {
  const container = document.getElementById("ingredientsListContainer");
  if (!container) return;

  const row = document.createElement("div");
  row.className = "dynamic-list-item flex items-center gap-2";

  const ingKeys = Object.keys(masterIngredients).sort();
  const optionsHtml = ingKeys.map((k) => {
    const item = masterIngredients[k];
    const name = item.display_name || item.name || k;
    const selected = (data.uid || data.ingredient_id) === k ? "selected" : "";
    return `<option value="${k}" ${selected}>${name} (${k})</option>`;
  }).join("");

  row.innerHTML = `
    <select aria-label="Ingredient" class="ing-select ${FIELD.selectSm} min-w-0 flex-[2]">
      <option value="">-- Select Master Ingredient --</option>
      ${optionsHtml}
    </select>
    <input type="text" aria-label="Amount" placeholder="Amount (2, 1/2)" value="${data.amount || data.quantity || ''}" class="ing-amount ${FIELD.inputSm} min-w-0 flex-1">
    <input type="text" aria-label="Unit" placeholder="Unit (cloves, cups)" value="${data.unit || data.display_unit || ''}" class="ing-unit ${FIELD.inputSm} min-w-0 flex-1">
    <button type="button" title="Remove this ingredient" onclick="this.parentNode.remove()" class="${BUTTON.iconDanger}"><span class="sr-only">Remove</span>${solidIcon("trash", "size-4")}</button>
  `;

  container.appendChild(row);
}

export function addInstructionRow(stepText = "") {
  const container = document.getElementById("instructionsListContainer");
  if (!container) return;

  const row = document.createElement("div");
  row.className = "dynamic-list-item flex items-center gap-2";

  row.innerHTML = `
    <input type="text" aria-label="Step" placeholder="Mince the garlic finely." value="${stepText}" class="inst-text ${FIELD.inputSm} min-w-0 flex-1">
    <button type="button" title="Remove this step" onclick="this.parentNode.remove()" class="${BUTTON.iconDanger}"><span class="sr-only">Remove</span>${solidIcon("trash", "size-4")}</button>
  `;

  container.appendChild(row);
}

export async function saveRecipe(db, catalogRecipes, masterIngredients, setDoc, doc, showToast, dismissToast, renderRecipeTable, getDoc = null, auth = null) {
  const title = document.getElementById("formTitle").value.trim();
  const slug = document.getElementById("formSlug").value.trim();
  if (!title || !slug) {
    showToast("Title and URL Slug are required.", "danger");
    return;
  }

  const recipeId = document.getElementById("formRecipeId").value.trim() || slug;
  const editable = await checkRecipeEditable(recipeId, { db, doc, getDoc });
  if (!editable.allowed) {
    showToast(editable.message, "danger");
    return;
  }
  const isSignature = document.getElementById("formIsSignature").checked;
  const recipeType = document.getElementById("formRecipeType").value;
  const formStatus = document.getElementById("formStatus")?.value;
  const urlInput = document.getElementById("formThumbnailUrl");

  const existing = catalogRecipes[recipeId] || activeCatalogRecipes[recipeId] || {};
  const status = formStatus || getRecipeStatus(existing) || "draft";
  const imageUrl = urlInput?.dataset?.imageUrl || urlInput?.value?.trim() || existing.image_url || "";
  const thumbnailUrl = urlInput?.dataset?.thumbnailUrl || existing.thumbnail_url || imageUrl;

  if (!imageUrl) {
    console.error("❌ [saveRecipe] Missing recipe image_url.");
    showToast("❌ Please generate or provide a recipe image before saving.", "danger");
    return;
  }

  // Gather Dynamic Ingredients
  const ingRows = document.querySelectorAll("#ingredientsListContainer .dynamic-list-item");
  const ingredients = [];
  ingRows.forEach((row) => {
    const uid = row.querySelector(".ing-select")?.value;
    const amount = row.querySelector(".ing-amount")?.value.trim();
    const unit = row.querySelector(".ing-unit")?.value.trim();
    if (uid) {
      const item = masterIngredients[uid] || {};
      ingredients.push({
        uid,
        name: item.display_name || item.name || uid,
        amount: amount || "1",
        unit: unit || item.display_unit || "gram"
      });
    }
  });

  // Gather Dynamic Instructions
  const instRows = document.querySelectorAll("#instructionsListContainer .dynamic-list-item");
  const instructions = [];
  instRows.forEach((row, idx) => {
    const text = row.querySelector(".inst-text")?.value.trim();
    if (text) {
      instructions.push({ step: idx + 1, instruction: text });
    }
  });

  const publishedAt = (status === "published")
    ? (existing.published_at || new Date().toISOString())
    : (existing.published_at || null);

  const payload = {
    id: recipeId,
    title,
    slug,
    status,
    recipe_type: recipeType,
    is_signature: isSignature,
    image_url: imageUrl,
    thumbnail_url: thumbnailUrl,
    prep_time_minutes: Number(document.getElementById("formPrepTime").value || 10),
    cook_time_minutes: Number(document.getElementById("formCookTime").value || 20),
    total_time_minutes: Number(document.getElementById("formPrepTime").value || 10) + Number(document.getElementById("formCookTime").value || 20),
    servings: Number(document.getElementById("formServings").value || 4),
    average_rating: Number(document.getElementById("formAverageRating").value || 4.8),
    allergen_flags: document.getElementById("formAllergens").value.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean),
    dietary_preferences: document.getElementById("formDietaryPreferences").value.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean),
    ingredients,
    instructions,
    published_at: publishedAt,
    updated_at: new Date().toISOString()
  };

  const rawSourceUrl = document.getElementById("formSourceUrl")?.value?.trim() || "";
  let sourceOrigin = null;
  if (rawSourceUrl) {
    let domain = "";
    try {
      domain = new URL(rawSourceUrl).hostname.replace(/^www\./, "");
    } catch (_) {}
    sourceOrigin = {
      type: "url",
      url: rawSourceUrl,
      domain: domain,
      captured_at: new Date().toISOString()
    };
  } else {
    sourceOrigin = {
      type: "manual",
      url: null,
      domain: null,
      captured_at: new Date().toISOString()
    };
  }

  catalogRecipes[recipeId] = payload;
  const processingToast = showToast(`⏳ Saving recipe '${title}' to Firestore...`, "processing");

  try {
    await setDoc(doc(db, "recipes", recipeId), payload, { merge: true });

    // Non-blocking audit log recording & admin summary source caching
    try {
      if (getDoc) {
        await recordRecipeAuditLog({
          db,
          setDoc,
          doc,
          getDoc,
          recipeId,
          action: ACTION_TYPES.RECIPE_EDITED,
          summary: "Updated recipe details in modal editor",
          details: {
            title,
            slug,
            status,
            is_signature: isSignature,
            recipe_type: recipeType,
            source_url: rawSourceUrl || null
          },
          source: sourceOrigin,
          currentUser: auth?.currentUser || (typeof auth === "object" && auth?.uid ? auth : null) || (typeof currentUser !== "undefined" ? currentUser : null)
        });
      }

      if (catalogRecipes && catalogRecipes[recipeId]) {
        catalogRecipes[recipeId].source_url = rawSourceUrl || null;
        catalogRecipes[recipeId].source_domain = sourceOrigin?.domain || null;
      }
      if (activeCatalogRecipes && activeCatalogRecipes[recipeId]) {
        activeCatalogRecipes[recipeId].source_url = rawSourceUrl || null;
        activeCatalogRecipes[recipeId].source_domain = sourceOrigin?.domain || null;
      }
    } catch (auditErr) {
      console.warn("⚠️ Non-blocking: Failed to record audit log or source link for recipe edit:", auditErr);
    }

    dismissToast(processingToast);
    hideDialog("recipeModal");
    if (renderRecipeTable) renderRecipeTable();
    showToast(`Saved recipe '${title}'!`, "success");
  } catch (err) {
    dismissToast(processingToast);
    console.error("❌ Save Recipe Error:", err);
    showToast("❌ Failed to save recipe: " + err.message, "danger");
  }
}

export async function deleteRecipe(id, db, catalogRecipes, deleteDoc, doc, showToast, dismissToast, renderRecipeTable) {
  if (!confirm(`Delete recipe '${id}'?`)) return;
  delete catalogRecipes[id];
  delete activeCatalogRecipes[id];
  selectedRecipeIds.delete(id);
  const processingToast = showToast(`⏳ Deleting recipe '${id}'...`, "processing");
  try {
    await deleteDoc(doc(db, "recipes", id));
    dismissToast(processingToast);
    if (renderRecipeTable) renderRecipeTable();
    showToast(`Deleted recipe '${id}'.`, "success");
  } catch (err) {
    dismissToast(processingToast);
    console.error("❌ Delete Recipe Error:", err);
    showToast("❌ Failed to delete recipe: " + err.message, "danger");
  }
}

export async function bulkDeleteRecipes(db, catalogRecipes, writeBatch, doc, showToast, dismissToast, renderRecipeTable) {
  const idsToDelete = Array.from(selectedRecipeIds);
  if (idsToDelete.length === 0) {
    if (showToast) showToast("No recipes selected for deletion.", "warning");
    return;
  }

  const count = idsToDelete.length;
  const msg = count === 1
    ? `Are you sure you want to permanently delete recipe '${idsToDelete[0]}'? This cannot be undone.`
    : `Are you sure you want to permanently delete ${count} selected recipes? This action cannot be undone.`;
  if (!confirm(msg)) return;

  const processingToast = showToast ? showToast(`⏳ Deleting ${count} recipe${count === 1 ? '' : 's'}...`, "processing") : null;
  try {
    const BATCH_SIZE = 450;
    for (let i = 0; i < idsToDelete.length; i += BATCH_SIZE) {
      const chunk = idsToDelete.slice(i, i + BATCH_SIZE);
      const batch = writeBatch(db);
      for (const id of chunk) {
        batch.delete(doc(db, "recipes", id));
      }
      await batch.commit();
    }

    for (const id of idsToDelete) {
      delete catalogRecipes[id];
      delete activeCatalogRecipes[id];
      selectedRecipeIds.delete(id);
    }

    if (dismissToast && processingToast) dismissToast(processingToast);
    if (renderRecipeTable) renderRecipeTable();
    if (showToast) showToast(`✅ Successfully deleted ${count} recipe${count === 1 ? '' : 's'}.`, "success");
  } catch (err) {
    if (dismissToast && processingToast) dismissToast(processingToast);
    console.error("❌ Bulk Delete Recipe Error:", err);
    if (showToast) showToast("❌ Failed to delete recipes: " + err.message, "danger");
  }
}

export async function bulkUpdateRecipeStatus(status, db, catalogRecipes, writeBatch, doc, showToast, dismissToast, renderRecipeTable) {
  const idsToUpdate = Array.from(selectedRecipeIds);
  if (idsToUpdate.length === 0) {
    if (showToast) showToast("No recipes selected.", "warning");
    return;
  }

  const count = idsToUpdate.length;
  const statusLabel = status === "published" ? "Published" : "Draft";
  const processingToast = showToast ? showToast(`⏳ Updating ${count} recipe${count === 1 ? '' : 's'} to ${statusLabel}...`, "processing") : null;

  try {
    const nowIso = new Date().toISOString();
    const BATCH_SIZE = 450;
    for (let i = 0; i < idsToUpdate.length; i += BATCH_SIZE) {
      const chunk = idsToUpdate.slice(i, i + BATCH_SIZE);
      const batch = writeBatch(db);
      for (const id of chunk) {
        const updateData = {
          status: status,
          updated_at: nowIso
        };
        const existing = catalogRecipes[id] || activeCatalogRecipes[id] || {};
        if (status === "published" && !existing.published_at) {
          updateData.published_at = nowIso;
        }
        batch.update(doc(db, "recipes", id), updateData);
      }
      await batch.commit();
    }

    for (const id of idsToUpdate) {
      if (catalogRecipes[id]) {
        catalogRecipes[id].status = status;
        catalogRecipes[id].updated_at = nowIso;
        if (status === "published" && !catalogRecipes[id].published_at) {
          catalogRecipes[id].published_at = nowIso;
        }
      }
      if (activeCatalogRecipes[id]) {
        activeCatalogRecipes[id].status = status;
        activeCatalogRecipes[id].updated_at = nowIso;
        if (status === "published" && !activeCatalogRecipes[id].published_at) {
          activeCatalogRecipes[id].published_at = nowIso;
        }
      }
    }

    if (dismissToast && processingToast) dismissToast(processingToast);
    if (renderRecipeTable) renderRecipeTable();
    if (showToast) showToast(`✅ Successfully marked ${count} recipe${count === 1 ? '' : 's'} as ${statusLabel}.`, "success");
  } catch (err) {
    if (dismissToast && processingToast) dismissToast(processingToast);
    console.error("❌ Bulk Update Status Error:", err);
    if (showToast) showToast("❌ Failed to update recipe status: " + err.message, "danger");
  }
}

export async function bulkUpdateRecipeSignature(isSignature, db, catalogRecipes, writeBatch, doc, showToast, dismissToast, renderRecipeTable) {
  const idsToUpdate = Array.from(selectedRecipeIds);
  if (idsToUpdate.length === 0) {
    if (showToast) showToast("No recipes selected.", "warning");
    return;
  }

  const count = idsToUpdate.length;
  const tierLabel = isSignature ? "Signature (Paywalled)" : "Free Catalog";
  const processingToast = showToast ? showToast(`⏳ Updating ${count} recipe${count === 1 ? '' : 's'} to ${tierLabel}...`, "processing") : null;

  try {
    const nowIso = new Date().toISOString();
    const BATCH_SIZE = 450;
    for (let i = 0; i < idsToUpdate.length; i += BATCH_SIZE) {
      const chunk = idsToUpdate.slice(i, i + BATCH_SIZE);
      const batch = writeBatch(db);
      for (const id of chunk) {
        batch.update(doc(db, "recipes", id), {
          is_signature: Boolean(isSignature),
          updated_at: nowIso
        });
      }
      await batch.commit();
    }

    for (const id of idsToUpdate) {
      if (catalogRecipes[id]) {
        catalogRecipes[id].is_signature = Boolean(isSignature);
        catalogRecipes[id].updated_at = nowIso;
      }
      if (activeCatalogRecipes[id]) {
        activeCatalogRecipes[id].is_signature = Boolean(isSignature);
        activeCatalogRecipes[id].updated_at = nowIso;
      }
    }

    if (dismissToast && processingToast) dismissToast(processingToast);
    if (renderRecipeTable) renderRecipeTable();
    if (showToast) showToast(`✅ Successfully set ${count} recipe${count === 1 ? '' : 's'} to ${tierLabel}.`, "success");
  } catch (err) {
    if (dismissToast && processingToast) dismissToast(processingToast);
    console.error("❌ Bulk Update Access Tier Error:", err);
    if (showToast) showToast("❌ Failed to update recipe access tier: " + err.message, "danger");
  }
}

function convertMeasurementInCrm(amount, fromUnit, toUnit) {
  const num = typeof amount === "number" ? amount : parseFloat(amount) || 0;
  if (num <= 0 || !fromUnit || !toUnit) return num;
  const f = fromUnit.toLowerCase().trim();
  const t = toUnit.toLowerCase().trim();
  if (f === t) return num;
  if ((f === "g" || f === "gram" || f === "grams") && (t === "g" || t === "gram" || t === "grams")) return num;

  // Volume: cup <-> tbsp <-> tsp
  if ((f === "cup" || f === "cups") && (t === "tbsp" || t === "tablespoon")) return Math.round(num * 16 * 100) / 100;
  if ((f === "tbsp" || f === "tablespoon") && (t === "cup" || t === "cups")) return Math.round((num / 16) * 100) / 100;
  if ((f === "tbsp" || f === "tablespoon") && (t === "tsp" || t === "teaspoon")) return Math.round(num * 3 * 100) / 100;
  if ((f === "tsp" || f === "teaspoon") && (t === "tbsp" || t === "tablespoon")) return Math.round((num / 3) * 100) / 100;

  // Weight: oz <-> gram, lb <-> gram
  if ((f === "oz" || f === "ounce") && (t === "gram" || t === "g")) return Math.round(num * 28.3495 * 10) / 10;
  if ((f === "lb" || f === "lbs" || f === "pound") && (t === "gram" || t === "g")) return Math.round(num * 453.592);

  return num;
}

export async function quickFixIngredientUnit(recipeId, targetUid, newUnit, db, setDoc, doc, showToast = null, dismissToast = null, getDoc = null) {
  if (!recipeId || !targetUid || !newUnit) return;
  if (!currentRecipeViewData || !currentRecipeViewData.ingredients) return;

  const targetIng = currentRecipeViewData.ingredients.find(i => (i.ingredient_id || i.uid) === targetUid);
  if (!targetIng) return;

  const oldUnit = targetIng.unit;
  const oldAmount = targetIng.amount;
  targetIng.unit = newUnit;
  targetIng.amount = convertMeasurementInCrm(oldAmount, oldUnit, newUnit);

  const ingName = targetIng.name_singular || targetIng.name || targetUid;
  let processingToast = null;
  if (showToast) processingToast = showToast(`⏳ Updating unit for '${ingName}' to '${newUnit}'...`, "processing");

  try {
    const docRef = doc(db, "recipes", recipeId);
    const updatedPayload = {
      ...currentRecipeViewData,
      ingredients: currentRecipeViewData.ingredients,
      updated_at: new Date().toISOString()
    };

    await setDoc(docRef, updatedPayload, { merge: true });

    if (dismissToast && processingToast) dismissToast(processingToast);
    if (showToast) showToast(`✓ Fixed unit for '${ingName}' to '${newUnit}'!`, "success");
    renderRecipeViewSection(db, getDoc, doc, showToast, activeCatalogRecipes);
  } catch (err) {
    if (dismissToast && processingToast) dismissToast(processingToast);
    console.error("❌ Quick Fix Unit Error:", err);
    targetIng.unit = oldUnit;
    targetIng.amount = oldAmount;
    if (showToast) showToast(`❌ Failed to update unit: ${err.message}`, "danger");
  }
}



// The Edit button of an inline card, and the Cancel and Save buttons that replace it while editing.
function inlineEditButton(id, toggleFn) {
  return `<button id="${id}" type="button" onclick="window.crmApp.${toggleFn}(true)" class="${BUTTON.small}">${solidIcon("pencil-square", "size-4 text-gray-400")}Edit</button>`;
}

function inlineEditButtons(toggleFn, saveFn) {
  return `<button type="button" onclick="window.crmApp.${toggleFn}(false)" class="${BUTTON.small}">Cancel</button><button type="button" onclick="window.crmApp.${saveFn}()" class="${BUTTON.smallPrimary}">Save</button>`;
}

export async function toggleInlineTaxonomyEdit(isEdit = true, categoriesData = activeCategoriesData, db = null, doc = null, getDoc = null) {
  const readGrid = document.getElementById("rvClassificationGrid");
  const editForm = document.getElementById("rvClassificationForm");
  const actionBtns = document.getElementById("rvTaxonomyActionBtns");

  if (!readGrid || !editForm || !actionBtns) return;

  if (isEdit) {
    if ((!activeCategoriesData || !activeCategoriesData.recipe_categories) && db && doc && getDoc) {
      try {
        const snap = await getDoc(doc(db, "globals", "categories"));
        if (snap.exists()) {
          activeCategoriesData = snap.data() || {};
        }
      } catch (err) {
        console.warn("On-demand categories fetch error:", err);
      }
    }

    populateInlineTaxonomyForm(activeCategoriesData);

    readGrid.style.display = "none";
    editForm.style.display = "flex";
    actionBtns.innerHTML = inlineEditButtons("toggleInlineTaxonomyEdit", "saveTaxonomy");
  } else {
    readGrid.style.display = "flex";
    editForm.style.display = "none";
    actionBtns.innerHTML = inlineEditButton("rvEditTaxonomyBtn", "toggleInlineTaxonomyEdit");
  }
}

// Kept for window.crmApp: the taxonomy pills style themselves with has-checked now (#1204).
export function updateTaxPillStyle() {}

export const DEFAULT_PROTEIN_OPTIONS = [
  "poultry",
  "beef",
  "pork",
  "seafood",
  "lamb",
  "vegetarian",
  "vegan",
  "none"
];

export function inferPrimaryProteinFromRecipe(recipe) {
  if (!recipe) return "";
  const directProtein = recipe.classification?.primary_protein || recipe.classification?.protein || recipe.primary_protein || recipe.protein;
  if (directProtein) return String(directProtein).trim().toLowerCase();

  const title = String(recipe.title || "").toLowerCase();
  const ingredients = Array.isArray(recipe.ingredients) ? recipe.ingredients : [];
  const ingNames = ingredients.map(i => {
    const id = String(i.ingredient_id || i.uid || "").toLowerCase();
    const name = String(i.name_singular || i.name || i.display_name || "").toLowerCase();
    return `${id} ${name}`;
  }).join(" ");

  const combined = `${title} ${ingNames}`;

  if (/\b(chicken|turkey|duck|poultry|cornish|hen)\b/.test(combined)) return "poultry";
  if (/\b(beef|steak|ground chuck|sirloin|ribeye|brisket|flank|tenderloin)\b/.test(combined)) return "beef";
  if (/\b(pork|bacon|ham|prosciutto|pancetta|chorizo|sausage|carnitas|pork chop)\b/.test(combined)) return "pork";
  if (/\b(salmon|shrimp|prawn|tuna|cod|tilapia|halibut|fish|crab|lobster|scallop|clam|mussel|calamari|seafood)\b/.test(combined)) return "seafood";
  if (/\b(lamb|mutton)\b/.test(combined)) return "lamb";

  const lifestyles = Array.isArray(recipe.classification?.lifestyle)
    ? recipe.classification.lifestyle
    : (recipe.classification?.lifestyle ? [recipe.classification.lifestyle] : []);
  const isVegan = lifestyles.some(l => String(l).toLowerCase().includes("vegan"));
  const isVeg = lifestyles.some(l => String(l).toLowerCase().includes("vegetarian"));

  if (/\b(tofu|tempeh|seitan|lentil|chickpea|black bean|edamame)\b/.test(combined)) {
    return isVegan ? "vegan" : "vegetarian";
  }

  if (isVegan) return "vegan";
  if (isVeg) return "vegetarian";

  return "none";
}

export function populateInlineTaxonomyForm(categoriesData = activeCategoriesData) {
  if (!currentRecipeViewData) return;

  const c = currentRecipeViewData.classification || {};
  const catObj = (categoriesData && Object.keys(categoriesData).length > 0) ? categoriesData : activeCategoriesData;
  const recipeCats = catObj.recipe_categories || [];

  const findCategoryTags = (catKey, currentValue = "") => {
    const cleanKey = String(catKey).trim().toLowerCase();
    const cleanVal = String(currentValue || "").trim().toLowerCase();

    let matchedCat = recipeCats.find(rc => {
      const id = String(rc.category_id || rc.id || "").trim().toLowerCase();
      const name = String(rc.name || "").trim().toLowerCase();
      return id === cleanKey || id.includes(cleanKey) || cleanKey.includes(id) || name.includes(cleanKey);
    });

    if ((!matchedCat || !Array.isArray(matchedCat.tags) || matchedCat.tags.length === 0) && cleanVal) {
      matchedCat = recipeCats.find(rc => {
        if (!Array.isArray(rc.tags)) return false;
        return rc.tags.some(t => {
          const tid = typeof t === "string" ? t : (t.id || t.tag_id || t.code || t.name);
          return String(tid || "").trim().toLowerCase() === cleanVal;
        });
      });
    }

    let tagsList = [];
    if (matchedCat && Array.isArray(matchedCat.tags) && matchedCat.tags.length > 0) {
      tagsList = matchedCat.tags;
    }

    if (tagsList.length > 0) {
      return tagsList.map(t => {
        if (typeof t === "string") {
          const formatted = t.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
          return { id: t, name: formatted };
        }
        const tid = t.id || t.tag_id || t.code || t.name || "";
        const tname = t.name || t.label || t.title || tid;
        return { id: tid, name: tname };
      });
    }

    return [];
  };

  const populateSelect = (selectId, catKey, currentValue) => {
    const el = document.getElementById(selectId);
    if (!el) return;

    const tags = findCategoryTags(catKey, currentValue);
    let html = `<option value="">-- None --</option>`;
    let foundCurrent = false;

    tags.forEach(t => {
      const isSel = String(t.id).trim().toLowerCase() === String(currentValue || "").trim().toLowerCase();
      if (isSel) foundCurrent = true;
      html += `<option value="${t.id}" ${isSel ? 'selected' : ''}>${t.name || t.id}</option>`;
    });

    if (currentValue && !foundCurrent) {
      let globalTag = null;
      for (const rc of recipeCats) {
        if (rc.tags && Array.isArray(rc.tags)) {
          const m = rc.tags.find(t => {
            const tid = typeof t === "string" ? t : (t.id || t.tag_id || t.code || t.name);
            return String(tid || "").trim().toLowerCase() === String(currentValue).trim().toLowerCase();
          });
          if (m) {
            globalTag = typeof m === "string" ? { id: m, name: m } : { id: m.id || m.tag_id || m.code, name: m.name || m.id };
            break;
          }
        }
      }

      if (globalTag) {
        html += `<option value="${globalTag.id}" selected>${globalTag.name || globalTag.id}</option>`;
      } else {
        html += `<option value="${currentValue}" selected>⚠️ ${currentValue} (missing from catalog)</option>`;
      }
    }

    el.innerHTML = html;
  };

  // Populate Protein Select (editable)
  const currentProtein = c.primary_protein || c.protein || c.primaryProtein || "";
  const protSelect = document.getElementById("taxEditProtein");
  if (protSelect) {
    let proteinTags = findCategoryTags("protein", currentProtein);
    if (proteinTags.length === 0) {
      proteinTags = DEFAULT_PROTEIN_OPTIONS.map(p => ({
        id: p,
        name: p.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
      }));
    }
    let html = `<option value="">-- None --</option>`;
    let foundCurrent = false;
    proteinTags.forEach(t => {
      const isSel = String(t.id).trim().toLowerCase() === String(currentProtein || "").trim().toLowerCase();
      if (isSel) foundCurrent = true;
      html += `<option value="${t.id}" ${isSel ? 'selected' : ''}>${t.name || t.id}</option>`;
    });
    if (currentProtein && !foundCurrent) {
      const formatted = currentProtein.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
      html += `<option value="${currentProtein}" selected>${formatted}</option>`;
    }
    protSelect.innerHTML = html;
  }

  populateSelect("taxEditCuisine", "cuisine", c.cuisine);
  populateSelect("taxEditMealType", "meal", c.meal_type);
  populateSelect("taxEditMethod", "method", c.method);
  populateSelect("taxEditEffort", "effort", c.effort);

  const renderPillItem = (id, label, isChecked, cbClass) => `
      <label class="${PILL}">
        <input type="checkbox" class="${cbClass} sr-only" value="${id}" ${isChecked ? 'checked' : ''}>
        <span>${label}</span>
      </label>
    `;

  const lifeContainer = document.getElementById("taxEditLifestyleContainer");
  if (lifeContainer) {
    const tags = findCategoryTags("lifestyle");
    const currentList = (Array.isArray(c.lifestyle) ? c.lifestyle : (c.lifestyle ? [c.lifestyle] : []))
      .map(x => String(x).trim().toLowerCase());

    if (tags.length > 0) {
      lifeContainer.innerHTML = tags.map(t => {
        const isChecked = currentList.includes(String(t.id).trim().toLowerCase());
        return renderPillItem(t.id, t.name || t.id, isChecked, "tax-lifestyle-cb");
      }).join("");
    } else if (currentList.length > 0) {
      lifeContainer.innerHTML = currentList.map(item => {
        const formatted = item.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
        return renderPillItem(item, formatted, true, "tax-lifestyle-cb");
      }).join("");
    } else {
      lifeContainer.innerHTML = `<span class="text-xs text-gray-500 dark:text-gray-400">No lifestyle tags in the categories</span>`;
    }
  }

  const occContainer = document.getElementById("taxEditOccasionContainer");
  if (occContainer) {
    const tags = findCategoryTags("occasion");
    const currentList = (Array.isArray(c.occasion) ? c.occasion : (c.occasion ? [c.occasion] : []))
      .map(x => String(x).trim().toLowerCase());

    if (tags.length > 0) {
      occContainer.innerHTML = tags.map(t => {
        const isChecked = currentList.includes(String(t.id).trim().toLowerCase());
        return renderPillItem(t.id, t.name || t.id, isChecked, "tax-occasion-cb");
      }).join("");
    } else if (currentList.length > 0) {
      occContainer.innerHTML = currentList.map(item => {
        const formatted = item.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
        return renderPillItem(item, formatted, true, "tax-occasion-cb");
      }).join("");
    } else {
      occContainer.innerHTML = `<span class="text-xs text-gray-500 dark:text-gray-400">No occasion tags in the categories</span>`;
    }
  }
}

export async function saveTaxonomy(db, setDoc, doc, showToast = null, dismissToast = null, getDoc = null) {
  if (!currentRecipeViewData) return;

  const hash = window.location.hash.replace("#", "").trim();
  let recipeId = "";
  if (hash.includes("?id=")) {
    recipeId = hash.split("?id=")[1].split("&")[0];
  } else if (hash.includes("id=")) {
    recipeId = new URLSearchParams(hash.split("?")[1]).get("id");
  } else {
    recipeId = new URLSearchParams(window.location.search).get("id");
  }
  recipeId = decodeURIComponent(recipeId || "").trim();

  if (!recipeId) return;

  const editable = await checkRecipeEditable(recipeId, { db, doc, getDoc, knownRecipe: currentRecipeViewData });
  if (!editable.allowed) {
    if (showToast) showToast(editable.message, "danger");
    return;
  }

  const cuisine = document.getElementById("taxEditCuisine")?.value || "";
  const primary_protein = document.getElementById("taxEditProtein")?.value || "";
  const meal_type = document.getElementById("taxEditMealType")?.value || "";
  const method = document.getElementById("taxEditMethod")?.value || "";
  const effort = document.getElementById("taxEditEffort")?.value || "";

  const lifestyle = Array.from(document.querySelectorAll(".tax-lifestyle-cb:checked")).map(el => el.value);
  const occasion = Array.from(document.querySelectorAll(".tax-occasion-cb:checked")).map(el => el.value);

  const updatedClassification = {
    ...(currentRecipeViewData.classification || {}),
    cuisine,
    primary_protein,
    protein: primary_protein,
    meal_type,
    method,
    effort,
    lifestyle,
    occasion
  };

  let processingToast = null;
  if (showToast) processingToast = showToast("⏳ Saving taxonomy...", "processing");

  try {
    const docRef = doc(db, "recipes", recipeId);
    await setDoc(docRef, {
      classification: updatedClassification,
      updated_at: new Date().toISOString()
    }, { merge: true });

    if (dismissToast && processingToast) dismissToast(processingToast);
    if (showToast) showToast("✓ Recipe taxonomy updated successfully!", "success");

    toggleInlineTaxonomyEdit(false);
    renderRecipeViewSection(db, getDoc, doc, showToast, activeCatalogRecipes, activeCategoriesData);
  } catch (err) {
    if (dismissToast && processingToast) dismissToast(processingToast);
    console.error("❌ Save Taxonomy Error:", err);
    if (showToast) showToast(`❌ Failed to update taxonomy: ${err.message}`, "danger");
  }
}

export function toggleInlineCookwareEdit(isEdit) {
  const readGrid = document.getElementById("rvHardwareChipsContainer");
  const editForm = document.getElementById("rvCookwareForm");
  const actionBtns = document.getElementById("rvCookwareActionBtns");

  if (!readGrid || !editForm || !actionBtns) return;

  if (isEdit) {
    const data = currentRecipeViewData || {};
    const gear = data.kitchen_hardware_requirements?.cookware_and_utensils || data.cookware_required || [];
    const input = document.getElementById("taxEditCookwareInput");
    if (input) {
      input.value = gear.join(", ");
    }

    readGrid.style.display = "none";
    editForm.style.display = "flex";
    actionBtns.innerHTML = inlineEditButtons("toggleInlineCookwareEdit", "saveCookware");
  } else {
    readGrid.style.display = "flex";
    editForm.style.display = "none";
    actionBtns.innerHTML = inlineEditButton("rvEditCookwareBtn", "toggleInlineCookwareEdit");
  }
}

export async function saveCookware(db, setDoc, doc, showToast = null, dismissToast = null, getDoc = null, auth = null) {
  if (!currentRecipeViewData) return;

  const hash = window.location.hash.replace("#", "").trim();
  let recipeId = "";
  if (hash.includes("?id=")) {
    recipeId = hash.split("?id=")[1].split("&")[0];
  } else if (hash.includes("id=")) {
    recipeId = new URLSearchParams(hash.split("?")[1]).get("id");
  } else {
    recipeId = new URLSearchParams(window.location.search).get("id");
  }
  recipeId = decodeURIComponent(recipeId || "").trim();

  if (!recipeId) return;

  const rawVal = document.getElementById("taxEditCookwareInput")?.value || "";
  const newGear = rawVal.split(",").map(s => s.trim()).filter(Boolean);

  const existingHardware = currentRecipeViewData.kitchen_hardware_requirements || {};

  let processingToast = null;
  if (showToast) processingToast = showToast("⏳ Saving cookware...", "processing");

  try {
    const docRef = doc(db, "recipes", recipeId);
    await setDoc(docRef, {
      kitchen_hardware_requirements: {
        ...existingHardware,
        cutting_boards_required: existingHardware.cutting_boards_required || 1,
        mixing_bowls_required: existingHardware.mixing_bowls_required || 1,
        cookware_required: newGear
      },
      updated_at: new Date().toISOString()
    }, { merge: true });

    // Non-blocking audit log recording
    try {
      if (getDoc) {
        await recordRecipeAuditLog({
          db,
          setDoc,
          doc,
          getDoc,
          recipeId,
          action: ACTION_TYPES.COOKWARE_UPDATED,
          summary: `Updated required cookware list (${newGear.length} items)`,
          details: {
            cookware_required: newGear,
            count: newGear.length
          },
          currentUser: auth?.currentUser || (typeof auth === "object" && auth?.uid ? auth : null) || (typeof currentUser !== "undefined" ? currentUser : null)
        });
      }
    } catch (auditErr) {
      console.warn("⚠️ Non-blocking: Failed to record audit log for cookware update:", auditErr);
    }

    if (dismissToast && processingToast) dismissToast(processingToast);
    if (showToast) showToast("✓ Required cookware updated successfully!", "success");

    toggleInlineCookwareEdit(false);
    renderRecipeViewSection(db, getDoc, doc, showToast, activeCatalogRecipes, activeCategoriesData);
  } catch (err) {
    if (dismissToast && processingToast) dismissToast(processingToast);
    console.error("❌ Save Cookware Error:", err);
    if (showToast) showToast(`❌ Failed to update cookware: ${err.message}`, "danger");
  }
}

export function toggleInlineMetricsEdit(isEdit) {
  const readGrid = document.getElementById("rvMetricsReadGrid");
  const editForm = document.getElementById("rvMetricsForm");
  const actionBtns = document.getElementById("rvMetricsActionBtns");

  if (!readGrid || !editForm || !actionBtns) return;

  if (isEdit) {
    const data = currentRecipeViewData || {};
    const metrics = data.planning_metrics || {};

    const prepVal = Number(metrics.prep_time_minutes || data.prep_time_minutes || 0);
    const cookVal = Number(metrics.cook_time_minutes || data.cook_time_minutes || 0);
    const servingsVal = Number(metrics.base_servings_adults || metrics.servings || data.base_servings_adults || data.servings || 4);
    const isSignature = Boolean(data.is_signature);

    const prepInput = document.getElementById("metEditPrepTime");
    if (prepInput) prepInput.value = prepVal;

    const cookInput = document.getElementById("metEditCookTime");
    if (cookInput) cookInput.value = cookVal;

    const servingsDisplay = document.getElementById("metDisplayServings");
    if (servingsDisplay) servingsDisplay.textContent = `${servingsVal} portions`;

    const tierSelect = document.getElementById("metEditTier");
    if (tierSelect) tierSelect.value = isSignature ? "paid" : "free";

    const secretSelect = document.getElementById("metEditSecret");
    if (secretSelect) secretSelect.value = Boolean(data.is_secret) ? "true" : "false";

    readGrid.style.display = "none";
    editForm.style.display = "flex";
    actionBtns.innerHTML = inlineEditButtons("toggleInlineMetricsEdit", "saveMetrics");
  } else {
    readGrid.style.display = "grid";
    editForm.style.display = "none";
    actionBtns.innerHTML = inlineEditButton("rvEditMetricsBtn", "toggleInlineMetricsEdit");
  }
}

export async function saveMetrics(db, setDoc, doc, showToast = null, dismissToast = null, getDoc = null) {
  if (!currentRecipeViewData) return;

  const hash = window.location.hash.replace("#", "").trim();
  let recipeId = "";
  if (hash.includes("?id=")) {
    recipeId = hash.split("?id=")[1].split("&")[0];
  } else if (hash.includes("id=")) {
    recipeId = new URLSearchParams(hash.split("?")[1]).get("id");
  } else {
    recipeId = new URLSearchParams(window.location.search).get("id");
  }
  recipeId = decodeURIComponent(recipeId || "").trim();

  if (!recipeId) return;

  const prepVal = Number(document.getElementById("metEditPrepTime")?.value || 0);
  const cookVal = Number(document.getElementById("metEditCookTime")?.value || 0);
  const tierVal = document.getElementById("metEditTier")?.value || "free";
  const secretVal = document.getElementById("metEditSecret")?.value === "true";

  const isSignature = tierVal === "paid";
  const totalTime = prepVal + cookVal;

  const existingMetrics = currentRecipeViewData.planning_metrics || {};

  let processingToast = null;
  if (showToast) processingToast = showToast("⏳ Saving metrics & visibility...", "processing");

  try {
    const docRef = doc(db, "recipes", recipeId);
    await setDoc(docRef, {
      prep_time_minutes: prepVal,
      cook_time_minutes: cookVal,
      total_time_minutes: totalTime,
      is_signature: isSignature,
      is_secret: secretVal,
      planning_metrics: {
        ...existingMetrics,
        prep_time_minutes: prepVal,
        cook_time_minutes: cookVal,
        total_time_minutes: totalTime
      },
      updated_at: new Date().toISOString()
    }, { merge: true });

    if (dismissToast && processingToast) dismissToast(processingToast);
    if (showToast) showToast("✓ Recipe metrics & tier updated successfully!", "success");

    toggleInlineMetricsEdit(false);
    renderRecipeViewSection(db, getDoc, doc, showToast, activeCatalogRecipes, activeCategoriesData);
  } catch (err) {
    if (dismissToast && processingToast) dismissToast(processingToast);
    console.error("❌ Save Metrics Error:", err);
    if (showToast) showToast(`❌ Failed to update metrics: ${err.message}`, "danger");
  }
}

function buildMasterIngredientSelectHtml(selectedId = "") {
  let masterMap = activeMasterIngredientsMap || {};
  if ((!masterMap || Object.keys(masterMap).length === 0) && typeof window !== "undefined" && window.crmApp && window.crmApp.getMasterIngredientsMap) {
    masterMap = window.crmApp.getMasterIngredientsMap() || {};
  }
  const sortedIds = Object.keys(masterMap).sort((a, b) => {
    const nameA = (masterMap[a].display_name || masterMap[a].name || a).toLowerCase();
    const nameB = (masterMap[b].display_name || masterMap[b].name || b).toLowerCase();
    return nameA.localeCompare(nameB);
  });

  const options = sortedIds.map(uid => {
    const item = masterMap[uid];
    const name = item.display_name || item.name || uid;
    const isSel = uid === selectedId ? "selected" : "";
    return `<option value="${uid}" ${isSel}>${name} (${uid})</option>`;
  });

  if (selectedId && !masterMap[selectedId]) {
    options.unshift(`<option value="${selectedId}" selected>⚠️ ${selectedId} (Custom / Unmapped)</option>`);
  } else {
    options.unshift(`<option value="">-- Select Master Ingredient --</option>`);
  }

  return options.join("");
}

export function addIngredientEditRow(ingData = {}) {
  const container = document.getElementById("rvIngredientsEditRowsContainer");
  if (!container) return;

  const uid = ingData.ingredient_id || ingData.uid || "";
  const amount = ingData.amount ?? "";
  const unit = ingData.unit ?? "";

  const rowId = "ing_row_" + Date.now() + "_" + Math.floor(Math.random() * 1000);
  const rowDiv = document.createElement("div");
  rowDiv.id = rowId;
  rowDiv.className = "ing-edit-row flex items-center gap-2 rounded-md bg-gray-50 p-2 dark:bg-white/5";

  rowDiv.innerHTML = `
    <select aria-label="Ingredient" class="ing-edit-id ${FIELD.selectSm} min-w-0 flex-[2]">${buildMasterIngredientSelectHtml(uid)}</select>
    <input type="number" aria-label="Quantity" placeholder="Qty" min="0" step="any" value="${amount}" class="ing-edit-amount ${FIELD.inputSm} min-w-0 flex-1">
    <input type="text" aria-label="Unit" placeholder="Unit" value="${unit}" class="ing-edit-unit ${FIELD.inputSm} min-w-0 flex-1">
    <button type="button" title="Remove this ingredient" onclick="document.getElementById('${rowId}')?.remove()" class="${BUTTON.iconDanger}"><span class="sr-only">Remove</span>${solidIcon("trash", "size-4")}</button>
  `;

  container.appendChild(rowDiv);
}

function populateIngredientsEditRows() {
  const container = document.getElementById("rvIngredientsEditRowsContainer");
  if (!container || !currentRecipeViewData) return;
  container.innerHTML = "";
  const ings = currentRecipeViewData.ingredients || [];
  if (ings.length === 0) {
    addIngredientEditRow();
  } else {
    ings.forEach(ing => addIngredientEditRow(ing));
  }
}

function buildInstructionStepRowHtml(quadrantId, stepNum, stepText = "") {
  const rowId = `quad_step_row_${quadrantId}_${Date.now()}_${Math.floor(Math.random() * 10000)}`;
  return `
    <div id="${rowId}" class="quad-step-row flex items-center gap-2">
      <span class="step-num-badge w-5 flex-none text-right text-xs font-semibold text-gray-500 dark:text-gray-400">${stepNum}.</span>
      <input type="text" aria-label="Step ${stepNum}" placeholder="What to do in this step" value="${stepText}" class="quad-step-input ${FIELD.inputSm} min-w-0 flex-1">
      <button type="button" title="Remove this step" onclick="document.getElementById('${rowId}')?.remove(); window.crmApp.reindexInstructionStepNumbers('${quadrantId}');" class="${BUTTON.iconDanger}"><span class="sr-only">Remove</span>${solidIcon("trash", "size-4")}</button>
    </div>
  `;
}

export function addInstructionStepEditRow(quadrantId, stepText = "") {
  const container = document.getElementById(`quad_steps_container_${quadrantId}`);
  if (!container) return;

  const currentCount = container.querySelectorAll(".quad-step-row").length;
  const stepNum = currentCount + 1;

  const wrapper = document.createElement("div");
  wrapper.innerHTML = buildInstructionStepRowHtml(quadrantId, stepNum, stepText);
  const rowEl = wrapper.firstElementChild;
  if (rowEl) container.appendChild(rowEl);
}

export function reindexInstructionStepNumbers(quadrantId) {
  const container = document.getElementById(`quad_steps_container_${quadrantId}`);
  if (!container) return;
  const badges = container.querySelectorAll(".step-num-badge");
  badges.forEach((b, idx) => {
    b.textContent = `${idx + 1}.`;
  });
}

function populateInstructionsMatrixEditGrid() {
  const container = document.getElementById("rvInstructionMatrixEditGrid");
  if (!container || !currentRecipeViewData) return;

  const timelines = currentRecipeViewData.instructions || {};
  const quickestTL = timelines.quickest_timeline || {};
  const dishSaverTL = timelines.min_dishes_timeline || timelines.dish_saver_timeline || {};

  const quadrants = [
    { id: "quickest_default", title: "Fastest, standard", tone: VARIANT_TONES.teal, variantData: quickestTL.default_variant || {} },
    { id: "dish_saver_default", title: "Dish Saver, standard", tone: VARIANT_TONES.sky, variantData: dishSaverTL.default_variant || {} }
  ];

  container.innerHTML = quadrants.map(q => {
    const v = q.variantData;
    const prep = v.prep_time ?? currentRecipeViewData.planning_metrics?.prep_time_minutes ?? 0;
    const cook = v.cook_time ?? currentRecipeViewData.planning_metrics?.cook_time_minutes ?? 0;
    const rawSteps = v.instructions || v.steps || [];

    const stepsRowsHtml = rawSteps.length > 0
      ? rawSteps.map((step, idx) => buildInstructionStepRowHtml(q.id, idx + 1, typeof step === "string" ? step : step.instruction || step.text || "")).join("")
      : buildInstructionStepRowHtml(q.id, 1, "");

    return `
      <div id="quad_edit_${q.id}" class="quad-edit-card ${q.tone.card}">
        <div class="${q.tone.head}"><span class="${q.tone.title}">${q.title}</span></div>
        <div class="flex gap-2">
          <div class="flex-1"><label class="${FIELD.labelSm}">Prep (min)</label><input type="number" min="0" value="${prep}" class="quad-edit-prep mt-1 ${FIELD.inputSm}"></div>
          <div class="flex-1"><label class="${FIELD.labelSm}">Cook (min)</label><input type="number" min="0" value="${cook}" class="quad-edit-cook mt-1 ${FIELD.inputSm}"></div>
        </div>
        <div>
          <span class="${FIELD.labelSm}">Steps</span>
          <div id="quad_steps_container_${q.id}" class="mt-1 flex flex-col gap-1.5">${stepsRowsHtml}</div>
          <button type="button" onclick="window.crmApp.addInstructionStepEditRow('${q.id}')" class="mt-2 ${BUTTON.small}">${solidIcon("plus", "size-4 text-gray-400")}Add a step</button>
        </div>
      </div>
    `;
  }).join("");
}

export function toggleUnifiedRecipeEdit(isEdit) {
  const actionHeader = document.getElementById("rvUnifiedActionHeader");
  const editBtn = document.getElementById("rvEditBtn");
  const rvTitle = document.getElementById("rvTitle");
  const rvTitleEditContainer = document.getElementById("rvTitleEditContainer");
  const rvTitleInput = document.getElementById("rvTitleInput");
  const ingReadBox = document.getElementById("rvIngReadBox");
  const ingEditBox = document.getElementById("rvIngEditBox");
  const matrixReadGrid = document.getElementById("rvInstructionMatrixGrid");
  const matrixEditGrid = document.getElementById("rvInstructionMatrixEditGrid");

  if (!currentRecipeViewData) return;

  if (isEdit) {
    if (editBtn) {
      editBtn.innerHTML = "Cancel editing";
      editBtn.onclick = () => toggleUnifiedRecipeEdit(false);
    }
    if (rvTitle) rvTitle.style.display = "none";
    if (rvTitleEditContainer) rvTitleEditContainer.style.display = "block";
    if (rvTitleInput) rvTitleInput.value = currentRecipeViewData.title || "";
    if (actionHeader) actionHeader.style.display = "flex";
    if (ingReadBox) ingReadBox.style.display = "none";
    if (ingEditBox) ingEditBox.style.display = "flex";
    if (matrixReadGrid) matrixReadGrid.style.display = "none";
    if (matrixEditGrid) matrixEditGrid.style.display = "grid";

    populateIngredientsEditRows();
    populateInstructionsMatrixEditGrid();
  } else {
    if (editBtn) {
      editBtn.innerHTML = `${solidIcon("pencil-square", "-ml-0.5 size-5 text-gray-400")}Edit recipe`;
      editBtn.onclick = () => window.crmApp.toggleUnifiedRecipeEdit(true); // through the edit guard in app.js
    }
    if (rvTitle) rvTitle.style.display = "block";
    if (rvTitleEditContainer) rvTitleEditContainer.style.display = "none";
    if (actionHeader) actionHeader.style.display = "none";
    if (ingReadBox) ingReadBox.style.display = "block";
    if (ingEditBox) ingEditBox.style.display = "none";
    if (matrixReadGrid) matrixReadGrid.style.display = "grid";
    if (matrixEditGrid) matrixEditGrid.style.display = "none";
  }
}

export async function saveUnifiedRecipe(db, setDoc, doc, showToast = null, dismissToast = null, getDoc = null, auth = null) {
  if (!currentRecipeViewData) return;

  const hash = window.location.hash.replace("#", "").trim();
  let recipeId = "";
  if (hash.includes("?id=")) {
    recipeId = hash.split("?id=")[1].split("&")[0];
  } else if (hash.includes("id=")) {
    recipeId = new URLSearchParams(hash.split("?")[1]).get("id");
  } else {
    recipeId = new URLSearchParams(window.location.search).get("id");
  }
  recipeId = decodeURIComponent(recipeId || "").trim();
  if (!recipeId) return;

  const editable = await checkRecipeEditable(recipeId, { db, doc, getDoc, knownRecipe: currentRecipeViewData });
  if (!editable.allowed) {
    if (showToast) showToast(editable.message, "danger");
    return;
  }

  // 1. Scrape Ingredients
  const ingRows = document.querySelectorAll("#rvIngredientsEditRowsContainer .ing-edit-row");
  const newIngredients = [];
  ingRows.forEach(row => {
    const id = row.querySelector(".ing-edit-id")?.value?.trim() || "";
    const amountVal = row.querySelector(".ing-edit-amount")?.value;
    const amount = amountVal !== "" && amountVal !== undefined ? Number(amountVal) : 0;
    const unit = row.querySelector(".ing-edit-unit")?.value?.trim() || "";

    if (id) {
      newIngredients.push({ ingredient_id: id, amount, unit });
    }
  });

  // 2. Scrape Instructions Matrix
  const scrapeQuadrant = (quadId) => {
    const card = document.getElementById(`quad_edit_${quadId}`);
    if (!card) return { prep_time: 0, cook_time: 0, instructions: [] };
    const prep = Number(card.querySelector(".quad-edit-prep")?.value || 0);
    const cook = Number(card.querySelector(".quad-edit-cook")?.value || 0);
    const stepInputs = card.querySelectorAll(".quad-step-input");
    const steps = [];
    stepInputs.forEach(input => {
      const txt = input.value.trim();
      if (txt) steps.push(txt);
    });
    return { prep_time: prep, cook_time: cook, instructions: steps };
  };

  const dishSaverData = {
    default_variant: scrapeQuadrant("dish_saver_default")
  };

  const newInstructions = {
    quickest_timeline: {
      default_variant: scrapeQuadrant("quickest_default")
    },
    min_dishes_timeline: dishSaverData,
    dish_saver_timeline: dishSaverData
  };

  let processingToast = null;
  if (showToast) processingToast = showToast("⏳ Saving all recipe content changes...", "processing");

  const titleInput = document.getElementById("rvTitleInput");
  const newTitle = titleInput ? titleInput.value.trim() : "";

  try {
    const docRef = doc(db, "recipes", recipeId);
    const payload = {
      ingredients: newIngredients,
      instructions: newInstructions,
      updated_at: new Date().toISOString()
    };
    if (newTitle) payload.title = newTitle;

    // A merge, maps included: what this editor no longer shows or edits since #2451 is kept as stored, the retired
    // Sunday Surge fields (`sunday_surge_pre_prep`, each timeline's `sunday_surge_variant`) and a Prep ahead version
    // (`instructions.prep_ahead_variant`, `prep_ahead`; whether it stays is open, #2454).
    await setDoc(docRef, payload, { merge: true });

    // Non-blocking audit log recording
    try {
      if (getDoc) {
        await recordRecipeAuditLog({
          db,
          setDoc,
          doc,
          getDoc,
          recipeId,
          action: ACTION_TYPES.RECIPE_EDITED,
          summary: "Updated recipe content in unified editor",
          details: {
            title: newTitle || currentRecipeViewData.title,
            ingredients_count: newIngredients.length
          },
          currentUser: auth?.currentUser || (typeof auth === "object" && auth?.uid ? auth : null) || (typeof currentUser !== "undefined" ? currentUser : null)
        });
      }
    } catch (auditErr) {
      console.warn("⚠️ Non-blocking: Failed to record audit log for unified recipe edit:", auditErr);
    }

    if (dismissToast && processingToast) dismissToast(processingToast);
    if (showToast) showToast("✓ All recipe content changes saved successfully!", "success");

    toggleUnifiedRecipeEdit(false);
    renderRecipeViewSection(db, getDoc, doc, showToast, activeMasterIngredientsMap, activeCategoriesData);
  } catch (err) {
    if (dismissToast && processingToast) dismissToast(processingToast);
    console.error("❌ Save Recipe Content Error:", err);
    if (showToast) showToast(`❌ Failed to save recipe changes: ${err.message}`, "danger");
  }
}

// -------------------------------------------------------------
// DEDICATED NEW RECIPE CREATOR (#sectionRecipeNew)
// -------------------------------------------------------------

let recipeNewTemplateLoaded = false;
let currentNewRecipeData = null;

export async function loadRecipeNewTemplate() {
  if (recipeNewTemplateLoaded) return;
  const container = document.getElementById("sectionRecipeNewContainer");
  if (!container) return;

  try {
    const res = await fetch("templates/recipeNew.html");
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    container.innerHTML = await res.text();
    recipeNewTemplateLoaded = true;
  } catch (err) {
    console.error("❌ Failed to load recipeNew.html template:", err);
  }
}

export async function renderRecipeNewSection() {
  await loadRecipeNewTemplate();
  window.location.hash = "#recipe-new";

  const secNew = document.getElementById("sectionRecipeNew");
  if (secNew) secNew.style.display = "block";

  ["sectionRecipes", "sectionDashboard", "sectionIngredients", "sectionCategories", "sectionDuplicates", "sectionRecipeView"].forEach((secId) => {
    const el = document.getElementById(secId);
    if (el) el.style.display = "none";
  });

  renderRecipeNewCookwareChips([]);
  onNewRecipeStatusChange("draft");
  initNewRecipeClassificationPills(currentNewRecipeData);
  // A recipe started by hand gets the two empty variants, so it can have steps (#1205).
  if (!document.querySelector("#rnInstructionMatrixGrid .quadrant-card")) renderNewRecipeInstructionGrid(null);
}

// The save buttons say what saving does, and turn green when it publishes.
export function onNewRecipeStatusChange(status = "draft") {
  const statusSelect = document.getElementById("rnStatusSelect");
  if (statusSelect && status && statusSelect.value !== status) {
    statusSelect.value = status;
  }

  const publishes = (status || (statusSelect ? statusSelect.value : "draft")) === "published";
  document.querySelectorAll(".rn-save-btn").forEach((btn) => {
    btn.textContent = publishes ? "Save and publish" : "Save as draft";
    btn.className = `rn-save-btn ${publishes ? BUTTON.success : BUTTON.primary}`;
  });
}

export function addNewRecipeIngredientRow(ingData = {}) {
  const container = document.getElementById("rnIngredientsRowsContainer");
  if (!container) return;

  const rowId = `rn_ing_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
  const row = document.createElement("div");
  row.id = rowId;
  row.className = "flex items-center gap-2 rounded-md bg-gray-50 p-2 dark:bg-white/5";

  const selectHtml = buildMasterIngredientSelectHtml(ingData.ingredient_id || ingData.uid || "");
  const amountVal = ingData.amount ?? 1;
  const unitVal = ingData.unit ?? "count";

  row.innerHTML = `
    <select aria-label="Ingredient" class="master-ing-select ${FIELD.selectSm} min-w-0 flex-[3]">${selectHtml}</select>
    <input type="number" aria-label="Quantity" step="any" min="0" value="${escapeHtml(amountVal)}" class="rn-ing-amount ${FIELD.inputSm} min-w-0 flex-1">
    <input type="text" aria-label="Unit" value="${escapeHtml(unitVal)}" class="rn-ing-unit ${FIELD.inputSm} min-w-0 flex-1">
    <button type="button" title="Remove this ingredient" onclick="document.getElementById('${rowId}')?.remove()" class="${BUTTON.iconDanger}"><span class="sr-only">Remove</span>${solidIcon("trash", "size-4")}</button>
  `;

  const selectElem = row.querySelector(".master-ing-select");
  const unitInput = row.querySelector(".rn-ing-unit");
  if (selectElem && unitInput) {
    selectElem.addEventListener("change", () => {
      const selectedUid = selectElem.value;
      if (selectedUid && activeMasterIngredientsMap && activeMasterIngredientsMap[selectedUid]) {
        const catItem = activeMasterIngredientsMap[selectedUid];
        const defUnit = catItem.display_unit;
        if (defUnit) {
          unitInput.value = defUnit;
        }
      }
    });
  }

  container.appendChild(row);
}

export function toggleNewRecipeRawJson() {
  const card = document.getElementById("rnRawJsonCard");
  if (!card) return;
  if (card.style.display === "none" || !card.style.display) {
    card.style.display = "block";
  } else {
    card.style.display = "none";
  }
}

export function copyNewRecipeRawJson(showToast = null) {
  const jsonPre = document.getElementById("rnRawJsonPre");
  const copyBtn = document.getElementById("rnCopyJsonBtn");
  if (!jsonPre) return;

  const txt = jsonPre.textContent;
  navigator.clipboard.writeText(txt).then(() => {
    if (copyBtn) copyBtn.textContent = "Copied";
    if (showToast) showToast("Copied the AI response.", "success");
    setTimeout(() => { if (copyBtn) copyBtn.textContent = "Copy JSON"; }, 2000);
  });
}

// A pill per tag, each a radio or checkbox that styles its pill with has-checked.
export function renderClassificationPillGroup(containerId, availableTags, selectedValues, inputClass, isRadio = false) {
  const container = document.getElementById(containerId);
  if (!container) return;

  const currentList = (Array.isArray(selectedValues) ? selectedValues : (selectedValues ? [selectedValues] : []))
    .map(x => String(x).trim().toLowerCase());

  const allTagMap = new Map();
  availableTags.forEach((t) => {
    const id = typeof t === "string" ? t : t.id;
    const name = typeof t === "string"
      ? t.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
      : (t.name || t.id);
    allTagMap.set(String(id).trim().toLowerCase(), { id, name });
  });

  currentList.forEach((item) => {
    if (item && !allTagMap.has(item)) {
      const formatted = item.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
      allTagMap.set(item, { id: item, name: formatted });
    }
  });

  container.innerHTML = Array.from(allTagMap.values()).map((t) => {
    const isChecked = currentList.includes(String(t.id).trim().toLowerCase());
    return `<label class="${PILL}"><input type="${isRadio ? "radio" : "checkbox"}" name="${containerId}_name" value="${escapeHtml(t.id)}" ${isChecked ? "checked" : ""} class="${inputClass} sr-only"><span>${escapeHtml(t.name)}</span></label>`;
  }).join("");
}

export function initNewRecipeClassificationPills(recipe = null) {
  const c = recipe?.classification || {};
  const proteinVal = c.primary_protein || c.protein || inferPrimaryProteinFromRecipe(recipe) || "";

  renderClassificationPillGroup(
    "rnCuisineContainer",
    ["american", "italian", "mexican", "asian", "mediterranean", "middle_eastern", "indian", "french", "japanese", "thai", "chinese"],
    c.cuisine,
    "rn-cuisine-cb",
    true
  );

  renderClassificationPillGroup(
    "rnProteinContainer",
    DEFAULT_PROTEIN_OPTIONS,
    proteinVal,
    "rn-protein-cb",
    true
  );

  renderClassificationPillGroup(
    "rnMealTypeContainer",
    ["main_course", "dinner", "lunch", "breakfast", "side_dish", "snack", "dessert"],
    c.meal_type,
    "rn-meal-cb",
    true
  );

  renderClassificationPillGroup(
    "rnEffortContainer",
    ["quick_and_easy", "weeknight_fast", "medium_effort", "weekend_project"],
    c.effort,
    "rn-effort-cb",
    true
  );

  renderClassificationPillGroup(
    "rnCarbCategoryContainer",
    ["stovetop", "oven", "sheet_pan", "one_pot", "no_cook", "grill", "air_fryer", "slow_cooker", "instant_pot"],
    c.method || c.carb_category,
    "rn-carb-cb",
    true
  );

  renderClassificationPillGroup(
    "rnSeasonalityContainer",
    ["weeknight", "comfort_food", "date_night", "crowd_pleaser", "meal_prep", "quick_lunch", "all_year", "summer", "fall", "winter", "spring"],
    c.occasion || c.seasonality,
    "rn-seasonality-cb",
    false
  );

  renderClassificationPillGroup(
    "rnLifestyleContainer",
    ["high_protein", "low_carb", "keto", "gluten_free", "dairy_free", "vegetarian", "vegan", "paleo", "kid_friendly"],
    c.lifestyle,
    "rn-lifestyle-cb",
    false
  );
}

export function renderRecipeNewCookwareChips(cookwareList = []) {
  const container = document.getElementById("rnCookwareChipsContainer");
  if (!container) return;

  const list = Array.isArray(cookwareList) ? cookwareList : [];
  if (list.length === 0) {
    container.innerHTML = `<span class="text-sm text-gray-500 dark:text-gray-400">No cookware added yet; standard cookware is assumed.</span>`;
    return;
  }

  // The Tailwind Plus badge with a remove button.
  container.innerHTML = list.map((item, idx) => `
    <span data-index="${idx}" data-cookware="${escapeHtml(item)}" class="rn-cookware-chip inline-flex items-center gap-x-1 rounded-md bg-gray-50 px-2 py-1 text-sm font-medium text-gray-700 inset-ring inset-ring-gray-500/10 dark:bg-gray-400/10 dark:text-gray-300 dark:inset-ring-gray-400/20">
      ${escapeHtml(item)}
      <button type="button" title="Remove ${escapeHtml(item)}" onclick="window.crmApp.removeRecipeNewCookwareChip(${idx})" class="group relative -mr-1 size-3.5 rounded-xs hover:bg-gray-500/20 dark:hover:bg-gray-500/30">
        <span class="sr-only">Remove</span>
        <svg viewBox="0 0 14 14" class="size-3.5 stroke-gray-600/50 group-hover:stroke-gray-600/75 dark:stroke-gray-400 dark:group-hover:stroke-gray-300"><path d="M4 4l6 6m0-6l-6 6" /></svg>
        <span class="absolute -inset-1"></span>
      </button>
    </span>
  `).join("");
}

export function getRecipeNewCookwareList() {
  const container = document.getElementById("rnCookwareChipsContainer");
  if (!container) return [];
  const chips = container.querySelectorAll(".rn-cookware-chip");
  const list = [];
  chips.forEach((chip) => {
    const val = chip.dataset.cookware?.trim();
    if (val) list.push(val);
  });
  return list;
}

export function addRecipeNewCookwareChip() {
  const input = document.getElementById("rnNewCookwareInput");
  if (!input) return;
  const val = input.value.trim();
  if (!val) return;

  const currentList = getRecipeNewCookwareList();
  if (!currentList.includes(val)) {
    currentList.push(val);
    renderRecipeNewCookwareChips(currentList);
  }
  input.value = "";
  input.focus();
}

export function removeRecipeNewCookwareChip(index) {
  const currentList = getRecipeNewCookwareList();
  if (index >= 0 && index < currentList.length) {
    currentList.splice(index, 1);
    renderRecipeNewCookwareChips(currentList);
  }
}

export function populateRecipeNewFromAiResponse(recipe, fullAiPayload = null) {
  if (!recipe) return;
  currentNewRecipeData = recipe;

  const jsonPre = document.getElementById("rnRawJsonPre");
  if (jsonPre) {
    jsonPre.textContent = JSON.stringify(fullAiPayload || recipe, null, 2);
  }

  const tokenUsage = fullAiPayload?.token_usage || recipe?.token_usage;
  const costBadge = document.getElementById("rnImportCostBadge");
  if (costBadge) {
    if (tokenUsage) {
      const costUsd = tokenUsage.estimated_cost_usd ?? tokenUsage.total?.estimated_cost_usd;
      const totalTokens = tokenUsage.total_tokens ?? tokenUsage.total?.total_tokens;
      const inTokens = tokenUsage.input_tokens ?? tokenUsage.total?.input_tokens;
      const outTokens = tokenUsage.output_tokens ?? tokenUsage.total?.output_tokens;
      if (costUsd != null && totalTokens != null) {
        costBadge.textContent = `AI import cost: ~$${Number(costUsd).toFixed(4)} (${totalTokens.toLocaleString()} tokens: ${inTokens ? inTokens.toLocaleString() : 0} in, ${outTokens ? outTokens.toLocaleString() : 0} out)`;
        costBadge.style.display = "inline-block";
      } else {
        costBadge.style.display = "none";
      }
    } else {
      costBadge.style.display = "none";
    }
  }

  const titleInput = document.getElementById("rnTitleInput");
  if (titleInput) titleInput.value = recipe.title || "Imported Recipe";

  const thumbInput = document.getElementById("rnThumbnailUrlInput");
  if (thumbInput) {
    thumbInput.value = recipe.image_url || recipe.thumbnail_url || "";
    thumbInput.dataset.imageUrl = recipe.image_url || "";
    thumbInput.dataset.thumbnailUrl = recipe.thumbnail_url || "";
  }

  const sourceInput = document.getElementById("rnSourceUrlInput");
  if (sourceInput) {
    sourceInput.value = recipe.source?.url || recipe.source_url || "";
  }

  const prepInput = document.getElementById("rnPrepTimeInput");
  if (prepInput) prepInput.value = recipe.planning_metrics?.prep_time_minutes || recipe.prep_time_minutes || 15;

  const cookInput = document.getElementById("rnCookTimeInput");
  if (cookInput) cookInput.value = recipe.planning_metrics?.cook_time_minutes || recipe.cook_time_minutes || 20;

  const servingsInput = document.getElementById("rnServingsInput");
  if (servingsInput) servingsInput.value = recipe.planning_metrics?.base_servings_adults || recipe.servings || 4;

  const sigSelect = document.getElementById("rnSignatureSelect");
  if (sigSelect) sigSelect.value = recipe.is_signature === true ? "signature" : "free";

  const secretSelect = document.getElementById("rnSecretSelect");
  if (secretSelect) secretSelect.value = recipe.is_secret === true ? "secret" : "public";

  // Pill-based Classification & Categorization
  initNewRecipeClassificationPills(recipe);

  // Required Cookware & Hardware
  const cookwareList = recipe.kitchen_hardware_requirements?.cookware_required
    || recipe.kitchen_hardware_requirements?.cookware_and_utensils
    || recipe.cookware_required
    || [];
  renderRecipeNewCookwareChips(cookwareList);

  // Master Ingredients
  const ingContainer = document.getElementById("rnIngredientsRowsContainer");
  if (ingContainer) {
    ingContainer.innerHTML = "";
    const ings = recipe.ingredients || [];
    ings.forEach((ing) => {
      addNewRecipeIngredientRow(ing);
    });
  }

  renderNewRecipeInstructionGrid(recipe);
}

// The two instruction variants of a new recipe, empty without one. saveNewRecipe reads them back
// from the quad_<id> cards: quad-prep, quad-cook and every edit-step-input. The Sunday Surge ones went
// with the feature (#2451).
function renderNewRecipeInstructionGrid(recipe) {
  const grid = document.getElementById("rnInstructionMatrixGrid");
  if (!grid) return;

  const inst = recipe?.instructions || {};
  const qt = inst.quickest_timeline || {};
  const dt = inst.min_dishes_timeline || inst.dish_saver_timeline || {};
  const quads = [
    { id: "quickest_default", title: "Fastest, standard", tone: VARIANT_TONES.teal, data: qt.default_variant || {} },
    { id: "dish_saver_default", title: "Dish Saver, standard", tone: VARIANT_TONES.sky, data: dt.default_variant || {} }
  ];

  grid.innerHTML = quads.map((q) => {
    const steps = Array.isArray(q.data.instructions) ? q.data.instructions : [];
    const stepsHtml = steps
      .map((step, idx) => newRecipeStepRowHtml(idx + 1, typeof step === "string" ? step : step?.instruction || step?.text || ""))
      .join("");
    return `
      <div id="quad_${q.id}" class="quadrant-card ${q.tone.card}">
        <div class="${q.tone.head}"><span class="${q.tone.title}">${q.title}</span></div>
        <div class="flex gap-2">
          <label class="flex-1"><span class="${FIELD.labelSm}">Prep (min)</span><input type="number" min="0" value="${escapeHtml(q.data.prep_time || 15)}" class="quad-prep mt-1 ${FIELD.inputSm}"></label>
          <label class="flex-1"><span class="${FIELD.labelSm}">Cook (min)</span><input type="number" min="0" value="${escapeHtml(q.data.cook_time || 20)}" class="quad-cook mt-1 ${FIELD.inputSm}"></label>
        </div>
        <div>
          <span class="${FIELD.labelSm}">Steps</span>
          <div id="rn_quad_steps_${q.id}" class="mt-1 flex flex-col gap-1.5">${stepsHtml}</div>
          <button type="button" onclick="window.crmApp.addNewRecipeStepRow('${q.id}')" class="mt-2 ${BUTTON.small}">${solidIcon("plus", "size-4 text-gray-400")}Add a step</button>
        </div>
      </div>
    `;
  }).join("");
}

function newRecipeStepRowHtml(stepNum, text = "") {
  return `
    <div class="rn-step-row flex items-start gap-2">
      <span class="rn-step-num w-5 flex-none pt-1 text-right text-xs/6 font-semibold text-gray-500 dark:text-gray-400">${stepNum}.</span>
      <textarea rows="2" aria-label="Step ${stepNum}" placeholder="What to do in this step" class="edit-step-input ${FIELD.inputSm} min-w-0 flex-1">${escapeHtml(text)}</textarea>
      <button type="button" title="Remove this step" onclick="window.crmApp.removeNewRecipeStepRow(this)" class="${BUTTON.iconDanger}"><span class="sr-only">Remove</span>${solidIcon("trash", "size-4")}</button>
    </div>
  `;
}

// The New Recipe page's own step buttons: addInstructionStepEditRow finds the recipe view's
// editor by id, which is in the page too once a recipe was opened (#1205).
export function addNewRecipeStepRow(quadId) {
  const container = document.getElementById(`rn_quad_steps_${quadId}`);
  if (!container) return;
  container.insertAdjacentHTML("beforeend", newRecipeStepRowHtml(container.querySelectorAll(".rn-step-row").length + 1));
  container.lastElementChild?.querySelector("textarea")?.focus();
}

export function removeNewRecipeStepRow(button) {
  const container = button?.closest("[id^='rn_quad_steps_']");
  button?.closest(".rn-step-row")?.remove();
  container?.querySelectorAll(".rn-step-row").forEach((row, idx) => {
    row.querySelector(".rn-step-num").textContent = `${idx + 1}.`;
    row.querySelector("textarea")?.setAttribute("aria-label", `Step ${idx + 1}`);
  });
}

export async function saveNewRecipe(db, setDoc, doc, showToast = null, dismissToast = null, collection = null, getDoc = null, auth = null) {
  const titleInput = document.getElementById("rnTitleInput");
  const titleVal = titleInput ? titleInput.value.trim() : "";

  if (!titleVal) {
    if (showToast) showToast("⚠️ Please enter a recipe title before saving.", "warning");
    return;
  }

  const prepVal = parseInt(document.getElementById("rnPrepTimeInput")?.value || "15", 10);
  const cookVal = parseInt(document.getElementById("rnCookTimeInput")?.value || "20", 10);
  const servingsVal = parseInt(document.getElementById("rnServingsInput")?.value || "4", 10);
  const isSig = document.getElementById("rnSignatureSelect")?.value === "signature";
  const isSecret = document.getElementById("rnSecretSelect")?.value === "secret";
  const statusSelect = document.getElementById("rnStatusSelect");
  const selectedStatus = statusSelect ? statusSelect.value : "draft";
  const status = (selectedStatus === "published" || selectedStatus === "draft") ? selectedStatus : "draft";

  // Scrape Ingredients
  const ingRows = document.querySelectorAll("#rnIngredientsRowsContainer > div");
  const ingredients = [];
  ingRows.forEach((row) => {
    const select = row.querySelector(".master-ing-select");
    const amountInput = row.querySelector(".rn-ing-amount");
    const unitInput = row.querySelector(".rn-ing-unit");

    const uid = select ? select.value : "";
    const amount = amountInput ? parseFloat(amountInput.value) || 1 : 1;
    const unit = unitInput ? unitInput.value.trim() : "count";

    if (uid) {
      ingredients.push({
        ingredient_id: uid,
        amount,
        unit,
        scaling_behavior: "linear",
        scaling_exponent: 1.0
      });
    }
  });

  // Scrape the Instruction Timelines
  const scrapeQuadrant = (quadId) => {
    const card = document.getElementById(`quad_${quadId}`);
    if (!card) return { prep_time: prepVal, cook_time: cookVal, instructions: [] };

    const prep = parseInt(card.querySelector(".quad-prep")?.value || prepVal, 10);
    const cook = parseInt(card.querySelector(".quad-cook")?.value || cookVal, 10);
    const stepInputs = card.querySelectorAll(".edit-step-input");
    const steps = [];
    stepInputs.forEach((input) => {
      const txt = input.value.trim();
      if (txt) steps.push(txt);
    });
    return { prep_time: prep, cook_time: cook, instructions: steps };
  };

  const dishSaverData = {
    default_variant: scrapeQuadrant("dish_saver_default")
  };

  const instructions = {
    quickest_timeline: {
      default_variant: scrapeQuadrant("quickest_default")
    },
    min_dishes_timeline: dishSaverData,
    dish_saver_timeline: dishSaverData
  };

  // Let Firestore decide document ID for new recipes
  const isExistingDoc = currentNewRecipeData && currentNewRecipeData.isExistingDoc === true && currentNewRecipeData.recipe_id;
  let docRef;
  if (isExistingDoc) {
    docRef = doc(db, "recipes", currentNewRecipeData.recipe_id);
  } else if (collection) {
    docRef = doc(collection(db, "recipes"));
  } else {
    const autoId = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `r_${Date.now()}`;
    docRef = doc(db, "recipes", autoId);
  }
  const recipeId = docRef.id;
  const slug = titleVal.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

  // Scrape Classification Metadata from Pill Selections (No dummy guesswork or hardcoded fallbacks!)
  const getCheckedPills = (selector) => {
    const checked = document.querySelectorAll(`${selector}:checked`);
    return Array.from(checked).map((inp) => inp.value);
  };

  const selectedCuisines = getCheckedPills(".rn-cuisine-cb");
  const selectedProteins = getCheckedPills(".rn-protein-cb");
  const selectedMealTypes = getCheckedPills(".rn-meal-cb");
  const selectedEfforts = getCheckedPills(".rn-effort-cb");
  const selectedCarbCategories = getCheckedPills(".rn-carb-cb");
  const selectedSeasonalities = getCheckedPills(".rn-seasonality-cb");
  const selectedLifestyles = getCheckedPills(".rn-lifestyle-cb");

  const classification = {
    ...(currentNewRecipeData?.classification || {})
  };
  if (selectedCuisines.length > 0) classification.cuisine = selectedCuisines[0];
  if (selectedProteins.length > 0) {
    classification.primary_protein = selectedProteins[0];
    classification.protein = selectedProteins[0];
  } else if (!classification.primary_protein) {
    const inferred = inferPrimaryProteinFromRecipe({
      title: titleVal,
      ingredients,
      classification
    });
    if (inferred) {
      classification.primary_protein = inferred;
      classification.protein = inferred;
    }
  }
  if (selectedMealTypes.length > 0) classification.meal_type = selectedMealTypes[0];
  if (selectedEfforts.length > 0) classification.effort = selectedEfforts[0];
  if (selectedCarbCategories.length > 0) {
    classification.method = selectedCarbCategories[0];
    classification.carb_category = selectedCarbCategories[0];
  } else if (!classification.method && classification.carb_category) {
    classification.method = classification.carb_category;
  }
  if (selectedSeasonalities.length > 0) {
    classification.occasion = selectedSeasonalities;
    classification.seasonality = selectedSeasonalities;
  } else if (!classification.occasion && classification.seasonality) {
    classification.occasion = Array.isArray(classification.seasonality) ? classification.seasonality : [classification.seasonality];
  }
  if (selectedLifestyles.length > 0) classification.lifestyle = selectedLifestyles;

  const urlInput = document.getElementById("rnThumbnailUrlInput");
  const imageUrl = currentNewRecipeData?.image_url || urlInput?.dataset?.imageUrl || urlInput?.value?.trim() || "";
  const thumbnailUrl = currentNewRecipeData?.thumbnail_url || urlInput?.dataset?.thumbnailUrl || imageUrl;

  if (!imageUrl) {
    console.error("❌ [saveNewRecipe] Missing recipe image_url.");
    if (showToast) showToast("❌ Please generate or provide a recipe image before saving.", "danger");
    return;
  }

  const publishedAt = (currentNewRecipeData && currentNewRecipeData.published_at)
    ? currentNewRecipeData.published_at
    : new Date().toISOString();

  const fullRecipeDoc = {
    ...(currentNewRecipeData || {}),
    recipe_id: recipeId,
    id: recipeId,
    title: titleVal,
    slug,
    status,
    recipe_type: "global",
    is_signature: isSig,
    is_secret: isSecret,
    image_url: imageUrl,
    thumbnail_url: thumbnailUrl,
    classification,
    prep_time_minutes: prepVal,
    cook_time_minutes: cookVal,
    total_time_minutes: prepVal + cookVal,
    base_servings_adults: servingsVal,
    planning_metrics: {
      ...(currentNewRecipeData?.planning_metrics || {}),
      prep_time_minutes: prepVal,
      cook_time_minutes: cookVal,
      total_time_minutes: prepVal + cookVal,
      base_servings_adults: servingsVal
    },
    ingredients,
    kitchen_hardware_requirements: {
      cutting_boards_required: currentNewRecipeData?.kitchen_hardware_requirements?.cutting_boards_required || 1,
      mixing_bowls_required: currentNewRecipeData?.kitchen_hardware_requirements?.mixing_bowls_required || 1,
      cookware_required: getRecipeNewCookwareList()
    },
    instructions,
    published_at: status === "published" ? publishedAt : null,
    created_at: (currentNewRecipeData && currentNewRecipeData.created_at) ? currentNewRecipeData.created_at : new Date().toISOString(),
    updated_at: new Date().toISOString()
  };

  const rawSourceUrl = document.getElementById("rnSourceUrlInput")?.value?.trim() || "";
  let sourceOrigin = null;
  if (rawSourceUrl) {
    let domain = "";
    try {
      domain = new URL(rawSourceUrl).hostname.replace(/^www\./, "");
    } catch (_) {}
    sourceOrigin = {
      type: "url",
      url: rawSourceUrl,
      domain: domain,
      captured_at: new Date().toISOString()
    };
  } else if (currentNewRecipeData?.source?.type === "text") {
    sourceOrigin = {
      type: "text",
      url: null,
      domain: null,
      captured_at: new Date().toISOString()
    };
  } else {
    sourceOrigin = {
      type: "manual",
      url: null,
      domain: null,
      captured_at: new Date().toISOString()
    };
  }

  let processingToast = null;
  if (showToast) processingToast = showToast(`⏳ Saving new recipe as ${status}...`, "processing");

  try {
    const docRef = doc(db, "recipes", recipeId);
    await setDoc(docRef, fullRecipeDoc);

    // Non-blocking audit log recording & admin summary source caching
    try {
      if (getDoc) {
        await recordRecipeAuditLog({
          db,
          setDoc,
          doc,
          getDoc,
          recipeId,
          action: ACTION_TYPES.RECIPE_CREATED,
          summary: rawSourceUrl ? `Created new recipe '${titleVal}' (${status}) from ${rawSourceUrl}` : `Created new recipe '${titleVal}' (${status})`,
          details: {
            title: titleVal,
            status,
            slug,
            source_url: rawSourceUrl || null
          },
          source: sourceOrigin,
          currentUser: auth?.currentUser || (typeof auth === "object" && auth?.uid ? auth : null) || (typeof currentUser !== "undefined" ? currentUser : null)
        });
      }

      if (activeCatalogRecipes) {
        if (!activeCatalogRecipes[recipeId]) {
          activeCatalogRecipes[recipeId] = {};
        }
        activeCatalogRecipes[recipeId].source_url = rawSourceUrl || null;
        activeCatalogRecipes[recipeId].source_domain = sourceOrigin?.domain || null;
      }
    } catch (auditErr) {
      console.warn("⚠️ Non-blocking: Failed to record audit log or source link for recipe creation:", auditErr);
    }

    if (dismissToast && processingToast) dismissToast(processingToast);
    if (showToast) showToast(`✓ Recipe '${titleVal}' created & saved as ${status}!`, "success");

    // Navigate to viewer page for newly created recipe
    window.location.hash = `#recipe-view?id=${recipeId}`;

    const secView = document.getElementById("sectionRecipeView");
    if (secView) secView.style.display = "block";

    ["sectionRecipes", "sectionDashboard", "sectionIngredients", "sectionCategories", "sectionDuplicates", "sectionRecipeNew"].forEach((secId) => {
      const el = document.getElementById(secId);
      if (el) el.style.display = "none";
    });

    renderRecipeViewSection(db, getDoc, doc, showToast, activeMasterIngredientsMap, activeCategoriesData);

  } catch (err) {
    if (dismissToast && processingToast) dismissToast(processingToast);
    console.error("❌ Save New Recipe Error:", err);
    if (showToast) showToast(`❌ Failed to save recipe: ${err.message}`, "danger");
  }
}

export function calculateImageGenerationCost(usageMetadata = {}) {
  const promptTokens = usageMetadata.promptTokenCount || 0;
  const candidateTokens = usageMetadata.candidatesTokenCount || 0;
  const totalTokens = usageMetadata.totalTokenCount || (promptTokens + candidateTokens);

  const promptCost = (promptTokens / 1_000_000) * 0.10;
  const imageCost = 0.039;
  const totalCost = promptCost + imageCost;

  return {
    promptTokens,
    candidateTokens,
    totalTokens,
    totalCost,
    formattedCost: `$${totalCost.toFixed(4)}`,
    summaryText: `Cost: ~$${totalCost.toFixed(3)} (${totalTokens} tokens)`
  };
}

export async function generateAiPhotoForRecipe(ai, getGenerativeModel, ResponseModality, functions, httpsCallable, showToast, dismissToast) {
  const btn = document.getElementById("btnGenerateAiPhoto") || document.getElementById("btnGenerateAiPhotoRn");
  const titleInput = document.getElementById("rnTitleInput") || document.getElementById("formTitle");
  const urlInput = document.getElementById("rnThumbnailUrlInput") || document.getElementById("formThumbnailUrl");

  const title = titleInput?.value?.trim() || currentRecipeViewData?.title || "";

  // Extract recipe text from instructions & ingredients fields or editor state
  let recipeText = "";
  if (currentNewRecipeData) {
    const ings = (currentNewRecipeData.ingredients || []).map(i => `${i.amount || ""} ${i.unit || ""} ${i.name_singular || i.ingredient_id}`).join(", ");
    const instrs = (currentNewRecipeData.instructions?.quickest_timeline?.default_variant?.instructions || []).join("\n");
    recipeText = `Ingredients: ${ings}\nInstructions: ${instrs}`;
  } else {
    const ingElements = document.querySelectorAll("#ingredientsListContainer .ingredient-name-input, #rnIngredientsRowsContainer .master-ing-select");
    const ingList = Array.from(ingElements).map(el => el.value).filter(Boolean).join(", ");
    const instrElements = document.querySelectorAll("#instructionsListContainer textarea, #rnInstructionMatrixGrid textarea");
    const instrList = Array.from(instrElements).map(el => el.value).filter(Boolean).join("\n");
    recipeText = `Ingredients: ${ingList}\nInstructions:\n${instrList}`;
  }

  if (!title) {
    if (showToast) showToast("❌ Please provide a Recipe Title before generating a photo.", "danger");
    return;
  }

  const promptText = `A natural, professional food photography shot of a finished single plate of the recipe "${title}". Medium shot showing the full plate centered with visible plate rims and natural table surroundings. Well-framed, uncropped, elegant presentation on a clean neutral table with soft natural light, suitable for a premium recipe application:

${recipeText}`;

  let originalBtnText = "🎨 Generate Photo with AI";
  if (btn) {
    originalBtnText = btn.innerHTML;
    btn.textContent = "Generating…";
    btn.disabled = true;
  }

  let processingToast = null;
  if (showToast) processingToast = showToast("📸 Generating AI recipe photo & uploading to Cloudinary...", "processing");

  try {
    let base64Image = "";

    // 1. Create a `GenerativeModel` instance configured for image response modality
    const modelOptions = {
      model: "gemini-3.1-flash-image"
    };

    if (ResponseModality && ResponseModality.IMAGE) {
      modelOptions.generationConfig = {
        responseModalities: [ResponseModality.IMAGE]
      };
    }

    const model = getGenerativeModel(ai, modelOptions);

    // 2. To generate an image, call `generateContent` with the text input
    const result = await model.generateContent(promptText);

    // 3. Extract generated image from inlineDataParts
    try {
      const inlineDataParts = result.response?.inlineDataParts?.() || [];
      if (inlineDataParts?.[0]) {
        const image = inlineDataParts[0].inlineData;
        base64Image = image?.data || image;
      }
    } catch (err) {
      console.warn("⚠️ inlineDataParts extraction warning:", err.message);
    }

    // Fallback extraction for candidate parts if needed
    if (!base64Image && result.response?.candidates?.[0]?.content?.parts?.[0]?.inlineData?.data) {
      base64Image = result.response.candidates[0].content.parts[0].inlineData.data;
    }

    // 4. Calculate generation cost from usageMetadata
    const usage = result.response?.usageMetadata || {};
    const costInfo = calculateImageGenerationCost(usage);

    // 5. Call Cloud Function to sign and upload base64 image to Cloudinary with telemetry
    const uploadFn = httpsCallable(functions, "uploadToCloudinary");
    const uploadRes = await uploadFn({
      imageBase64: base64Image,
      recipeId: title,
      usageMetadata: usage
    });

    const imageUrl = uploadRes.data?.image_url;
    const thumbnailUrl = uploadRes.data?.thumbnail_url || imageUrl;
    if (!imageUrl) {
      throw new Error("Cloudinary upload failed to return image URL.");
    }

    if (urlInput) {
      urlInput.value = imageUrl;
      urlInput.dataset.imageUrl = imageUrl;
      urlInput.dataset.thumbnailUrl = thumbnailUrl;
    }

    if (currentNewRecipeData) {
      currentNewRecipeData.image_url = imageUrl;
      currentNewRecipeData.thumbnail_url = thumbnailUrl;
    }

    const costBadge = document.getElementById("rnPhotoCostBadge");
    if (costBadge) {
      costBadge.textContent = costInfo.summaryText;
      costBadge.style.display = "inline-block";
    }

    if (dismissToast && processingToast) dismissToast(processingToast);
    if (showToast) showToast(`✅ Recipe photo generated & uploaded! (${costInfo.summaryText})`, "success");

    notifyPhotoReady(title);

  } catch (err) {
    if (dismissToast && processingToast) dismissToast(processingToast);
    console.error("❌ AI Recipe Photo Generation Error:", err);
    if (showToast) showToast(`❌ Photo generation failed: ${err.message}`, "danger");
    notifyRecipeError(`Photo generation failed: ${err.message}`);
  } finally {
    if (btn) {
      btn.innerHTML = originalBtnText;
      btn.disabled = false;
    }
  }
}

export async function regenerateRecipePhoto(db, setDoc, doc, ai, getGenerativeModel, ResponseModality, functions, httpsCallable, showToast, dismissToast, getDoc = null, auth = null) {
  if (!currentRecipeViewData) {
    if (showToast) showToast("❌ No active recipe data loaded.", "danger");
    return;
  }

  const hash = window.location.hash.replace("#", "").trim();
  let recipeId = "";
  if (hash.includes("?id=")) {
    recipeId = hash.split("?id=")[1].split("&")[0];
  } else if (hash.includes("id=")) {
    recipeId = new URLSearchParams(hash.split("?")[1]).get("id");
  } else {
    recipeId = new URLSearchParams(window.location.search).get("id");
  }
  recipeId = decodeURIComponent(recipeId || currentRecipeViewData.recipe_id || currentRecipeViewData.id || "").trim();

  if (!recipeId) {
    if (showToast) showToast("❌ Recipe ID not found.", "danger");
    return;
  }

  const title = currentRecipeViewData.title || recipeId;

  // 1. Format ingredients into a clean text bullet list (not JSON)
  const ingList = currentRecipeViewData.ingredients || [];
  const ingLines = ingList.map(i => {
    const amt = i.amount != null ? `${i.amount} ` : "";
    const unit = i.unit ? `${i.unit} ` : "";
    const name = i.name_singular || i.name || i.display_name || i.ingredient_id || i.uid || "";
    return `- ${amt}${unit}${name}`.trim();
  }).filter(Boolean).join("\n");

  // 2. Extract one of the recipe variants (Fastest or first available)
  let variantSteps = [];
  const instrs = currentRecipeViewData.instructions;
  if (instrs?.quickest_timeline?.default_variant?.instructions?.length) {
    variantSteps = instrs.quickest_timeline.default_variant.instructions;
  } else if (instrs?.dish_saver_timeline?.default_variant?.instructions?.length) {
    variantSteps = instrs.dish_saver_timeline.default_variant.instructions;
  } else if (Array.isArray(instrs)) {
    variantSteps = instrs.map(s => typeof s === "string" ? s : s.instruction || s.step || "");
  } else if (typeof instrs === "object") {
    for (const timelineKey of Object.keys(instrs)) {
      const timeline = instrs[timelineKey];
      if (timeline?.default_variant?.instructions?.length) {
        variantSteps = timeline.default_variant.instructions;
        break;
      }
    }
  }

  const stepLines = variantSteps.map((step, idx) => `${idx + 1}. ${step}`).join("\n");

  const styleSelect = document.getElementById("rvPhotoStyleSelect");
  const selectedStyleId = styleSelect?.value || "random";

  const { promptText, selectedStyle } = buildRecipePhotoPromptWithMetadata(
    title,
    currentRecipeViewData.ingredients || [],
    variantSteps,
    { style: selectedStyleId }
  );

  const btn = document.getElementById("rvRegeneratePhotoBtn");
  let originalBtnText = "🎨 Regenerate Photo with AI";
  if (btn) {
    originalBtnText = btn.innerHTML;
    btn.innerHTML = `📸 Generating (${selectedStyle.name})...`;
    btn.disabled = true;
  }

  let processingToast = null;
  if (showToast) processingToast = showToast(`📸 Generating AI food photo (${selectedStyle.name})...`, "processing");

  try {
    let base64Image = "";

    const modelOptions = {
      model: "gemini-3.1-flash-image"
    };

    if (ResponseModality && ResponseModality.IMAGE) {
      modelOptions.generationConfig = {
        responseModalities: [ResponseModality.IMAGE]
      };
    }

    const model = getGenerativeModel(ai, modelOptions);
    const result = await model.generateContent(promptText);

    try {
      const inlineDataParts = result.response?.inlineDataParts?.() || [];
      if (inlineDataParts?.[0]) {
        const image = inlineDataParts[0].inlineData;
        base64Image = image?.data || image;
      }
    } catch (err) {
      console.warn("⚠️ inlineDataParts extraction warning:", err.message);
    }

    if (!base64Image && result.response?.candidates?.[0]?.content?.parts?.[0]?.inlineData?.data) {
      base64Image = result.response.candidates[0].content.parts[0].inlineData.data;
    }

    // Calculate generation cost from usageMetadata
    const usage = result.response?.usageMetadata || {};
    const costInfo = calculateImageGenerationCost(usage);

    // Call Cloud Function to sign and upload base64 image to Cloudinary with telemetry
    const uploadFn = httpsCallable(functions, "uploadToCloudinary");
    const uploadRes = await uploadFn({
      imageBase64: base64Image,
      recipeId: title,
      usageMetadata: usage
    });

    const imageUrl = uploadRes.data?.image_url;
    const thumbnailUrl = uploadRes.data?.thumbnail_url || imageUrl;
    if (!imageUrl) {
      throw new Error("Cloudinary upload failed to return image URL.");
    }

    // Atomically persist to Firestore document
    const docRef = doc(db, "recipes", recipeId);
    await setDoc(docRef, {
      image_url: imageUrl,
      thumbnail_url: thumbnailUrl,
      updated_at: new Date().toISOString()
    }, { merge: true });

    // Non-blocking audit log recording
    try {
      if (getDoc) {
        await recordRecipeAuditLog({
          db,
          setDoc,
          doc,
          getDoc,
          recipeId,
          action: ACTION_TYPES.PHOTO_REGENERATED,
          summary: `Regenerated AI photo with Gemini & Cloudinary (${selectedStyle.name})`,
          details: {
            image_url: imageUrl,
            thumbnail_url: thumbnailUrl,
            photo_style: selectedStyle.id,
            photo_style_name: selectedStyle.name,
            cost: costInfo?.formattedCost || null,
            tokens: costInfo?.totalTokens || null
          },
          currentUser: auth?.currentUser || (typeof auth === "object" && auth?.uid ? auth : null) || (typeof currentUser !== "undefined" ? currentUser : null)
        });
      }
    } catch (auditErr) {
      console.warn("⚠️ Non-blocking: Failed to record audit log for photo regeneration:", auditErr);
    }

    // Update in-memory state and DOM
    currentRecipeViewData.image_url = imageUrl;
    currentRecipeViewData.thumbnail_url = thumbnailUrl;

    const heroImg = document.getElementById("rvHeroImage");
    if (heroImg) {
      heroImg.src = imageUrl;
    }

    const jsonPre = document.getElementById("rvJsonPre");
    if (jsonPre) {
      jsonPre.textContent = JSON.stringify(currentRecipeViewData, null, 2);
    }

    const costBadge = document.getElementById("rvPhotoCostBadge");
    if (costBadge) {
      costBadge.textContent = `${costInfo.summaryText} • ${selectedStyle.name}`;
      costBadge.style.display = "block";
    }

    // Refresh audit log timeline in viewer DOM
    if (getDoc) {
      await refreshRecipeAuditLog(db, doc, getDoc, recipeId);
    }

    if (dismissToast && processingToast) dismissToast(processingToast);
    if (showToast) showToast(`✅ Recipe photo regenerated with "${selectedStyle.name}" style! (${costInfo.summaryText})`, "success");

    notifyPhotoReady(currentRecipeViewData?.title);

  } catch (err) {
    if (dismissToast && processingToast) dismissToast(processingToast);
    console.error("❌ Regenerate Recipe Photo Error:", err);
    if (showToast) showToast(`❌ Photo regeneration failed: ${err.message}`, "danger");
    notifyRecipeError(`Photo regeneration failed: ${err.message}`);
  } finally {
    if (btn) {
      btn.innerHTML = originalBtnText;
      btn.disabled = false;
    }
  }
}

/**
 * Triggers the hidden file input dialog for local recipe photo upload.
 * @param {"modal" | "new" | "view"} context 
 */
export function triggerRecipePhotoUpload(context = "view") {
  let fileInput = null;
  if (context === "modal") {
    fileInput = document.getElementById("formPhotoFileInput");
  } else if (context === "new") {
    fileInput = document.getElementById("rnPhotoFileInput");
  } else {
    fileInput = document.getElementById("rvPhotoFileInput");
  }

  if (fileInput) {
    fileInput.value = ""; // Reset to ensure change event fires even if same file selected
    fileInput.click();
  } else {
    console.warn(`⚠️ [triggerRecipePhotoUpload] File input not found for context: '${context}'`);
  }
}

/**
 * Handles uploading a local image file from the computer to Cloudinary via uploadToCloudinary Cloud Function.
 * Updates DOM inputs, in-memory models, and Firestore if in Recipe View context.
 *
 * @param {Event} event 
 * @param {"modal" | "new" | "view"} context 
 * @param {Object} deps Injected dependencies: { functions, httpsCallable, showToast, dismissToast, db, doc, setDoc, getDoc, auth }
 */
export async function uploadRecipePhotoFromLocal(event, context = "view", deps = {}) {
  const { functions, httpsCallable, showToast, dismissToast, db, doc, setDoc, getDoc, auth } = deps;
  const file = event?.target?.files?.[0];
  if (!file) return;

  // 1. Validate MIME type
  const validTypes = ["image/jpeg", "image/png", "image/webp", "image/jpg"];
  if (!validTypes.includes(file.type.toLowerCase())) {
    if (showToast) showToast("❌ Please select a valid image file (JPEG, PNG, or WebP).", "danger");
    if (event.target) event.target.value = "";
    return;
  }

  // 2. Validate file size (max 10MB)
  const maxBytes = 10 * 1024 * 1024;
  if (file.size > maxBytes) {
    if (showToast) showToast("❌ Image file size exceeds the 10MB limit.", "danger");
    if (event.target) event.target.value = "";
    return;
  }

  // Determine button to show loading spinner/text
  let btn = null;
  let originalBtnText = "📁 Upload Local Photo";
  if (context === "modal") {
    btn = document.getElementById("btnUploadPhotoModal");
  } else if (context === "new") {
    btn = document.getElementById("btnUploadPhotoRn");
  } else {
    btn = document.getElementById("rvUploadPhotoBtn");
  }

  if (btn) {
    originalBtnText = btn.innerHTML;
    btn.textContent = "Uploading…";
    btn.disabled = true;
  }

  let processingToast = null;
  if (showToast) processingToast = showToast(`⏳ Uploading "${file.name}" to Cloudinary...`, "processing");

  try {
    // 3. Read file as Base64 Data URL
    const base64Data = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = (err) => reject(new Error("Failed to read image file."));
      reader.readAsDataURL(file);
    });

    // Determine recipe title / id for public_id slug
    let recipeIdentifier = "recipe";
    if (context === "new") {
      recipeIdentifier = document.getElementById("rnTitleInput")?.value?.trim() || "new_recipe";
    } else if (context === "modal") {
      recipeIdentifier = document.getElementById("formTitle")?.value?.trim() || document.getElementById("formRecipeId")?.value?.trim() || "recipe";
    } else {
      recipeIdentifier = currentRecipeViewData?.title || currentRecipeViewData?.id || "recipe";
    }

    // 4. Call Cloud Function to upload signed image to Cloudinary
    const uploadFn = httpsCallable(functions, "uploadToCloudinary");
    const uploadRes = await uploadFn({
      imageBase64: base64Data,
      recipeId: recipeIdentifier
    });

    const imageUrl = uploadRes.data?.image_url;
    const thumbnailUrl = uploadRes.data?.thumbnail_url || imageUrl;
    if (!imageUrl) {
      throw new Error("Cloudinary upload failed to return image URL.");
    }

    // 5. Update appropriate views based on context
    if (context === "modal") {
      const thumbInput = document.getElementById("formThumbnailUrl");
      if (thumbInput) {
        thumbInput.value = imageUrl;
        thumbInput.dataset.imageUrl = imageUrl;
        thumbInput.dataset.thumbnailUrl = thumbnailUrl;
      }
    } else if (context === "new") {
      const rnThumbInput = document.getElementById("rnThumbnailUrlInput");
      if (rnThumbInput) {
        rnThumbInput.value = imageUrl;
        rnThumbInput.dataset.imageUrl = imageUrl;
        rnThumbInput.dataset.thumbnailUrl = thumbnailUrl;
      }
      if (currentNewRecipeData) {
        currentNewRecipeData.image_url = imageUrl;
        currentNewRecipeData.thumbnail_url = thumbnailUrl;
      }
    } else {
      // "view" context: persist directly to Firestore & audit log
      const hash = window.location.hash.replace("#", "").trim();
      let recipeId = "";
      if (hash.includes("?id=")) {
        recipeId = hash.split("?id=")[1].split("&")[0];
      } else if (hash.includes("id=")) {
        recipeId = new URLSearchParams(hash.split("?")[1]).get("id");
      } else {
        recipeId = new URLSearchParams(window.location.search).get("id");
      }
      recipeId = decodeURIComponent(recipeId || currentRecipeViewData?.recipe_id || currentRecipeViewData?.id || "").trim();

      if (recipeId && db && doc && setDoc) {
        const docRef = doc(db, "recipes", recipeId);
        await setDoc(docRef, {
          image_url: imageUrl,
          thumbnail_url: thumbnailUrl,
          updated_at: new Date().toISOString()
        }, { merge: true });

        // Record Audit Log event
        try {
          if (getDoc) {
            await recordRecipeAuditLog({
              db,
              setDoc,
              doc,
              getDoc,
              recipeId,
              action: ACTION_TYPES.PHOTO_UPLOADED,
              summary: `Uploaded local photo "${file.name}" to Cloudinary`,
              details: {
                image_url: imageUrl,
                thumbnail_url: thumbnailUrl,
                file_name: file.name,
                file_size_bytes: file.size,
                file_type: file.type
              },
              currentUser: auth?.currentUser || (typeof auth === "object" && auth?.uid ? auth : null) || (typeof currentUser !== "undefined" ? currentUser : null)
            });
          }
        } catch (auditErr) {
          console.warn("⚠️ Non-blocking: Failed to record audit log for photo upload:", auditErr);
        }

        // Update in-memory viewer data & DOM
        if (currentRecipeViewData) {
          currentRecipeViewData.image_url = imageUrl;
          currentRecipeViewData.thumbnail_url = thumbnailUrl;
        }

        const heroImg = document.getElementById("rvHeroImage");
        if (heroImg) {
          heroImg.src = imageUrl;
        }

        const jsonPre = document.getElementById("rvJsonPre");
        if (jsonPre && currentRecipeViewData) {
          jsonPre.textContent = JSON.stringify(currentRecipeViewData, null, 2);
        }

        // Update catalog cache if present
        if (typeof activeCatalogRecipes !== "undefined" && activeCatalogRecipes[recipeId]) {
          activeCatalogRecipes[recipeId].image_url = imageUrl;
          activeCatalogRecipes[recipeId].thumbnail_url = thumbnailUrl;
        }

        // Refresh audit log timeline
        if (getDoc) {
          await refreshRecipeAuditLog(db, doc, getDoc, recipeId);
        }
      }
    }

    if (dismissToast && processingToast) dismissToast(processingToast);
    if (showToast) showToast(`✅ Recipe photo "${file.name}" uploaded successfully!`, "success");

    notifyPhotoReady(recipeIdentifier);

  } catch (err) {
    if (dismissToast && processingToast) dismissToast(processingToast);
    console.error("❌ Local Photo Upload Error:", err);
    if (showToast) showToast(`❌ Photo upload failed: ${err.message}`, "danger");
    notifyRecipeError(`Photo upload failed: ${err.message}`);
  } finally {
    if (btn) {
      btn.innerHTML = originalBtnText;
      btn.disabled = false;
    }
    if (event?.target) {
      event.target.value = "";
    }
  }
}

/**
 * Opens the 'Add Recipe from JSON' modal and ensures a clean state or default template.
 */
export function openAddRecipeJsonModal() {
  const modal = document.getElementById("addRecipeJsonModal");
  if (!modal) return;

  const errEl = document.getElementById("addRecipeJsonError");
  if (errEl) {
    errEl.style.display = "none";
    errEl.textContent = "";
  }

  const fileNameEl = document.getElementById("addRecipeJsonFileName");
  if (fileNameEl) fileNameEl.style.display = "none";

  showDialog("addRecipeJsonModal");

  const textarea = document.getElementById("addRecipeJsonTextarea");
  if (textarea) {
    if (!textarea.value.trim()) {
      pasteExampleRecipeJson();
    }
    textarea.focus();
  }
}

/**
 * Loads an authoritative example RecipeDocument payload (with empty ID) into the JSON editor.
 */
export function pasteExampleRecipeJson() {
  const textarea = document.getElementById("addRecipeJsonTextarea");
  if (!textarea) return;

  const exampleDoc = {
    id: "",
    title: "Greek Chicken Souvlaki Skillet with Warm Garlic Naan",
    slug: "greek-chicken-souvlaki-skillet",
    status: "published",
    recipe_type: "global",
    is_signature: false,
    is_secret: false,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    published_at: new Date().toISOString(),
    created_by: null,
    image_url: "",
    thumbnail_url: "",
    source_type: "text_draft",
    source_domain: "",
    full_url: "",
    allergens: ["dairy", "wheat"],
    dietary_preferences: [],
    classification: {
      cuisine: "mediterranean",
      meal_type: "dinner",
      primary_protein: "chicken",
      method: "stovetop",
      carb_category: "bread",
      time_effort: ["under_30_mins", "quick_and_easy"],
      goals_lifestyles: ["high_protein"],
      occasions_seasons: ["weeknight_dinner"]
    },
    prep_time_minutes: 15,
    cook_time_minutes: 15,
    total_time_minutes: 30,
    dish_load_score: 2,
    base_servings_adults: 2,
    difficulty_level: "easy",
    cookware: [
      "10-inch skillet",
      "chef's knife",
      "cutting board",
      "tongs"
    ],
    ingredients: [
      {
        ingredient_id: "chicken_breast",
        name_singular: "Boneless Skinless Chicken Breast",
        amount: 340,
        unit: "g",
        category: "poultry",
        prep_note: "cut into 1-inch bite-sized pieces",
        is_optional: false,
        is_staple: false
      },
      {
        ingredient_id: "olive_oil",
        name_singular: "Extra Virgin Olive Oil",
        amount: 15,
        unit: "ml",
        category: "oils_fats",
        is_staple: true
      },
      {
        ingredient_id: "greek_seasoning",
        name_singular: "Greek Seasoning Blend",
        amount: 5,
        unit: "g",
        category: "spices_seasonings",
        is_staple: true
      },
      {
        ingredient_id: "tzatziki_sauce",
        name_singular: "Tzatziki Sauce",
        amount: 60,
        unit: "g",
        category: "dairy_eggs",
        is_staple: false
      }
    ],
    instructions: {
      fastest_variant: {
        prep_time_minutes: 15,
        cook_time_minutes: 15,
        total_time_minutes: 30,
        dish_load_score: 2,
        cookware: ["10-inch skillet", "chef's knife", "cutting board", "tongs"],
        steps: [
          {
            step_number: 1,
            instruction: "Cut [quantity:340,unit:g] chicken breast into 1-inch bite-sized pieces on a cutting board.",
            ingredients_callouts: [
              {
                ingredient_id: "chicken_breast",
                name: "Chicken Breast",
                amount: 340,
                unit: "g"
              }
            ]
          },
          {
            step_number: 2,
            instruction: "Heat [quantity:15,unit:ml] olive oil in a 10-inch skillet over medium-high heat [1 minute].",
            ingredients_callouts: [
              {
                ingredient_id: "olive_oil",
                name: "Olive Oil",
                amount: 15,
                unit: "ml"
              }
            ]
          },
          {
            step_number: 3,
            instruction: "Add chicken and [quantity:5,unit:g] Greek seasoning. Cook stirring occasionally until golden and cooked through [6 to 8 minutes].",
            ingredients_callouts: [
              {
                ingredient_id: "greek_seasoning",
                name: "Greek Seasoning",
                amount: 5,
                unit: "g"
              }
            ]
          }
        ]
      },
      dish_saver_variant: {
        prep_time_minutes: 15,
        cook_time_minutes: 15,
        total_time_minutes: 30,
        dish_load_score: 1,
        cookware: ["10-inch skillet", "chef's knife", "cutting board"],
        steps: [
          {
            step_number: 1,
            instruction: "Pre-season diced chicken directly in skillet before placing on burner to save bowls."
          }
        ]
      }
    }
  };

  textarea.value = JSON.stringify(exampleDoc, null, 2);
}

/**
 * Drag-and-drop support for the "Add Recipe from JSON" modal: lets an admin
 * drop a .json file directly onto the payload editor instead of pasting.
 */
export function handleRecipeJsonDragOver(event) {
  event.preventDefault();
  event.dataTransfer.dropEffect = "copy";
  document.getElementById("addRecipeJsonDropzone")?.classList.add("drag-active");
}

export function handleRecipeJsonDragLeave(event) {
  event.preventDefault();
  // Ignore bubbled dragleave events fired while moving between child elements
  // (e.g. the textarea) so the highlight doesn't flicker while dragging over it.
  const dropzone = document.getElementById("addRecipeJsonDropzone");
  if (dropzone && event.relatedTarget && dropzone.contains(event.relatedTarget)) return;
  dropzone?.classList.remove("drag-active");
}

export function handleRecipeJsonDrop(event) {
  event.preventDefault();
  document.getElementById("addRecipeJsonDropzone")?.classList.remove("drag-active");

  const errEl = document.getElementById("addRecipeJsonError");
  const textarea = document.getElementById("addRecipeJsonTextarea");
  const fileNameEl = document.getElementById("addRecipeJsonFileName");
  if (!textarea) return;

  const showError = (msg) => {
    if (errEl) {
      errEl.textContent = msg;
      errEl.style.display = "block";
    }
  };

  const file = event.dataTransfer?.files?.[0];
  if (!file) return;

  if (!file.name.toLowerCase().endsWith(".json") && file.type !== "application/json") {
    showError(`"${file.name}" is not a .json file. Please drop a valid RecipeDocument JSON file.`);
    return;
  }

  if (errEl) errEl.style.display = "none";

  const reader = new FileReader();
  reader.onload = () => {
    textarea.value = String(reader.result || "");
    if (fileNameEl) {
      fileNameEl.textContent = `📄 Loaded: ${file.name}`;
      fileNameEl.style.display = "inline";
    }
  };
  reader.onerror = () => {
    showError(`Failed to read "${file.name}". Please try again or paste the JSON manually.`);
  };
  reader.readAsText(file);
}

/**
 * Validates, assigns short document ID, and writes RecipeDocument to Firestore.
 */
export async function submitAddRecipeJson(db, setDoc, doc, collection, showToast = null, dismissToast = null, auth = null, getDoc = null) {
  const errEl = document.getElementById("addRecipeJsonError");
  const textarea = document.getElementById("addRecipeJsonTextarea");
  const submitBtn = document.getElementById("btnAddRecipeJsonSubmit");

  const showError = (msg) => {
    if (errEl) {
      errEl.textContent = msg;
      errEl.style.display = "block";
    }
    if (showToast) showToast(`❌ ${msg}`, "danger");
  };

  if (!textarea) return;
  const rawText = textarea.value.trim();
  if (!rawText) {
    showError("Please paste a valid RecipeDocument JSON payload.");
    return;
  }

  let recipe;
  try {
    recipe = JSON.parse(rawText);
  } catch (parseErr) {
    showError(`Invalid JSON Syntax: ${parseErr.message}`);
    return;
  }

  if (typeof recipe !== "object" || recipe === null || Array.isArray(recipe)) {
    showError("JSON root must be a valid RecipeDocument object.");
    return;
  }

  if (!recipe.title || typeof recipe.title !== "string" || !recipe.title.trim()) {
    showError("Recipe 'title' is required and cannot be empty.");
    return;
  }

  // Generate short Firestore Document ID if empty or missing
  let recipeId = (recipe.id && typeof recipe.id === "string") ? recipe.id.trim() : "";
  if (!recipeId) {
    try {
      if (collection && db) {
        const generatedDocRef = doc(collection(db, "recipes"));
        recipeId = generatedDocRef.id;
      } else if (doc && db) {
        const generatedDocRef = doc(db, "recipes", (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID().replace(/-/g, "").substring(0, 20) : `r_${Date.now()}`);
        recipeId = generatedDocRef.id;
      }
    } catch (genErr) {
      console.warn("Could not generate Firestore ID via collection(), falling back to crypto ID:", genErr);
      recipeId = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID().replace(/-/g, "").substring(0, 20) : `r_${Date.now()}`;
    }
  }

  recipe.id = recipeId;

  // Cleanup & Schema Conformance
  delete recipe.recipe_id; // Drop legacy field per modern schema
  delete recipe.leftovers_logic; // Drop legacy leftovers logic per modern schema

  const nowIso = new Date().toISOString();
  recipe.created_at = recipe.created_at || nowIso;
  recipe.updated_at = nowIso;
  recipe.status = (recipe.status === "published" || recipe.status === "draft" || recipe.status === "archived") ? recipe.status : "published";
  if (recipe.status === "published" && !recipe.published_at) {
    recipe.published_at = nowIso;
  }

  recipe.recipe_type = recipe.recipe_type || "global";
  recipe.is_signature = Boolean(recipe.is_signature);
  recipe.is_secret = Boolean(recipe.is_secret);

  if (!recipe.slug) {
    recipe.slug = recipe.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  }

  const currentUser = auth?.currentUser;
  if (!recipe.created_by && currentUser) {
    recipe.created_by = currentUser.email || currentUser.uid || null;
  }

  // Normalize top-level metrics
  recipe.base_servings_adults = typeof recipe.base_servings_adults === "number" ? recipe.base_servings_adults : 2;
  recipe.prep_time_minutes = typeof recipe.prep_time_minutes === "number" ? recipe.prep_time_minutes : (parseInt(recipe.prep_time_minutes, 10) || 0);
  recipe.cook_time_minutes = typeof recipe.cook_time_minutes === "number" ? recipe.cook_time_minutes : (parseInt(recipe.cook_time_minutes, 10) || 0);
  recipe.total_time_minutes = typeof recipe.total_time_minutes === "number" ? recipe.total_time_minutes : (recipe.prep_time_minutes + recipe.cook_time_minutes);
  recipe.dish_load_score = typeof recipe.dish_load_score === "number" ? recipe.dish_load_score : (parseInt(recipe.dish_load_score, 10) || 1);
  recipe.difficulty_level = recipe.difficulty_level || "easy";

  // Arrays
  recipe.allergens = Array.isArray(recipe.allergens) ? recipe.allergens : [];
  recipe.dietary_preferences = Array.isArray(recipe.dietary_preferences) ? recipe.dietary_preferences : [];
  recipe.cookware = Array.isArray(recipe.cookware) ? recipe.cookware : [];
  recipe.ingredients = Array.isArray(recipe.ingredients) ? recipe.ingredients : [];

  // Disable button while saving
  const origBtnHtml = submitBtn ? submitBtn.innerHTML : "";
  if (submitBtn) {
    submitBtn.innerHTML = `⏳ Saving to Firestore...`;
    submitBtn.disabled = true;
  }

  try {
    const targetDocRef = doc(db, "recipes", recipe.id);
    await setDoc(targetDocRef, recipe);

    // Non-blocking Audit Log recording
    try {
      if (getDoc) {
        await recordRecipeAuditLog({
          db,
          setDoc,
          doc,
          getDoc,
          recipeId: recipe.id,
          action: ACTION_TYPES.RECIPE_CREATED,
          summary: `Created Recipe '${recipe.title}' via JSON Import with ID: ${recipe.id}`,
          details: { method: "json_import", title: recipe.title, status: recipe.status },
          currentUser: currentUser
        });
      }
    } catch (auditErr) {
      console.warn("⚠️ Non-blocking: Could not record audit log:", auditErr);
    }

    // Update active catalog recipes state & table
    activeCatalogRecipes[recipe.id] = recipe;
    renderRecipeTable(activeCatalogRecipes);

    // Close Modal
    hideDialog("addRecipeJsonModal");
    if (errEl) errEl.style.display = "none";
    textarea.value = "";

    if (showToast) showToast(`✅ Recipe '${recipe.title}' successfully added with ID: ${recipe.id}!`, "success");

    // Open Recipe View for the newly created recipe
    viewRecipe(recipe.id);

  } catch (saveErr) {
    console.error("❌ Failed to save recipe JSON to Firestore:", saveErr);
    showError(`Firestore Error: ${saveErr.message}`);
  } finally {
    if (submitBtn) {
      submitBtn.innerHTML = origBtnHtml;
      submitBtn.disabled = false;
    }
  }
}

